import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useChatContext } from '../../contexts/ChatContext';
import { useCallContext } from '../../contexts/CallContext';
import FloatingChat from '../chat/FloatingChat';
import WhatsAppBubble from '../whatsapp/WhatsAppBubble';
import TicketsBubble from '../tickets/TicketsBubble';
import reportClientError from '../../utils/reportClientError';
import { NOTIFICATION_SOUNDS, SOUND_EVENTS, SOUND_GROUPS, getSoundFor, setSoundFor, playSound, playFor } from '../../utils/notificationSounds';
import CompanyLogo from '../CompanyLogo';
import IncomingCallOverlay from '../chat/IncomingCallOverlay';
import ActiveCallOverlay from '../chat/ActiveCallOverlay';
import RemoteAssistBubble from '../RemoteAssistBubble';
import ImpersonationBanner from '../ImpersonationBanner';
import WhatsNewPopover from '../WhatsNewPopover';
import PendingReviewBanner from '../PendingReviewBanner';
import AgentActionsModal from '../AgentActionsModal';
import KeyboardShortcutsHelp from '../KeyboardShortcutsHelp';
import AdminViewAsWidget from '../AdminViewAsWidget';
import FloatingProjectTimer from '../FloatingProjectTimer';
import PokemonOverlay from '../PokemonOverlay';
import WelcomeHelpButton from '../WelcomeHelpButton';
import PendingActionsBell from '../PendingActionsBell';
import CommandPalette from '../CommandPalette';
import VersionPill from '../VersionPill';
import ProfileCompletionModal from '../ProfileCompletionModal';
import AgentOnboardingModal from '../AgentOnboardingModal';
import TenantWelcomeWizard, { debeVerBienvenida } from '../TenantWelcomeWizard';
import GuidedTour from '../GuidedTour';
import CalendarConnectionBanner from '../CalendarConnectionBanner';
import MobileBottomNav from './MobileBottomNav';
import QuickBugModal from '../QuickBugModal';
import PinnedTicketsBar from '../PinnedTicketsBar';
import AlertTicker from '../AlertTicker';
import InstanceSwitcher from '../InstanceSwitcher';
import PlatformLauncher from '../PlatformLauncher';
import ActionNeededBanner from '../ActionNeededBanner';
import useIsMobile from '../../hooks/useIsMobile';
import { SUPPLY_CLIENTS } from '../../config/supplyClients';
import useConsoleErrorBuffer from '../../hooks/useConsoleErrorBuffer';
import { TRACKED_MODULES, moduleFromPath } from '../../utils/moduleFromPath';
import { startDwell, track } from '../../utils/trackAction';
import { installHeatmap } from '../../utils/heatmap';
import { Zap, MousePointer2,
  LayoutDashboard, Ticket, Users, Wallet, Settings, Gamepad2, Trophy,
  PanelLeftClose, PanelLeftOpen,
  User, LogOut, Menu, X, Globe, ChevronDown, Building2,
  Inbox, Trash2, AlertOctagon, Circle, Activity, TrendingUp, Bell, Check, Radio, Monitor,
  Sun, Moon, Mail, KeyRound, Volume2, VolumeX, UserCheck, Printer, BarChart3, Flame, Package, Video, MessageSquare, MessageCircle, Upload,
  Receipt, Landmark, Cpu, HardDrive, ShieldCheck, BookOpen, FileText, CalendarDays, Star, History, Rocket,
  GripVertical, RotateCcw, Eye, Truck, Phone, Plus, Pencil, Copy, ClipboardList, Fingerprint, Clock, CheckCircle2, Keyboard, Search, ClipboardCheck, ScreenShare, Shield, Lightbulb, Sparkles, UserPlus, Filter, RefreshCw, Send, ListChecks, Network, FolderLock, Wrench, CalendarCheck, Compass,
  List, Layers, ArrowDownAZ, BellRing, BellOff, ChevronUp, LayoutGrid, Server, Megaphone, Smartphone, Cog, Speaker, Cloud, Home, FileSpreadsheet, ShoppingCart, Bot, Scale, Store, FileBarChart
} from 'lucide-react';
import {
  isOsNotifSupported, isOsNotifEnabled, isOsNotifOnlyWhenHidden, getOsNotifPermission,
  requestOsNotifPermission, setOsNotifEnabled, setOsNotifOnlyWhenHidden,
  tryOsNotify, isIosSafari, isStandalonePwa,
  isWebPushSupported, subscribeWebPush, unsubscribeWebPush,
} from '../../utils/osNotifications';

/* Detect iOS/Android standalone PWA mode */
function isStandaloneMode() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
}

import api from '../../utils/api';
import { onTicketsChanged } from '../../utils/ticketEvents';
import { getBrand, cargarBrand } from '../../utils/brand';
import PoweredByAyudaTecno from '../PoweredByAyudaTecno';
import { canUseSupplierAccounts } from '../../pages/SupplierAccounts/supplierUtils';
import { canUseMyPlatforms } from '../../pages/MyPlatforms/myPlatformsUtils';
import { canUseCostsHub } from '../../pages/CostsHub/costsHubUtils';
import { io as socketIO } from 'socket.io-client';
import DockDropHint from '../DockDropHint';

/* ── Module visit tracking ── */

/* ── Sidebar view modes ── */
const NAV_VIEW_MODES = ['default', 'custom', 'categories', 'alphabetical'];

/* ── Category mapping for "Categories" view mode ──
   Each item key maps to one category. Items not listed fall into 'other'. */
const NAV_CATEGORY_MAP = {
  // Main
  dashboard: 'main', cockpit: 'main', game: 'main', prode: 'main',
  attendance: 'operations',
  // Operations
  group_tickets: 'operations', companies: 'operations', client_access: 'operations',
  ticket_import: 'admin', storage: 'admin',
  calendar: 'operations', errands: 'operations', requests: 'operations', report_review: 'operations',
  // Inventory
  assets: 'inventory', workshop: 'inventory', prepaid_lines: 'inventory', legacy_sheets: 'knowledge', group_supplies: 'inventory',
  assets: 'inventory', workshop: 'inventory', prepaid_lines: 'inventory', mercadolibre: 'operations', google_business: 'comms', group_supplies: 'inventory',
  cctv: 'inventory', vault: 'inventory', configurations: 'inventory', racks: 'inventory',
  '2fa_codes': 'inventory', deployments: 'inventory',
  // Monitoring & Network
  monitor: 'monitoring', monitoring: 'monitoring', smart_homes: 'monitoring',
  recruiting: 'admin', group_health_checks: 'monitoring',
  dmarc: 'monitoring',
  topology: 'monitoring', vpn: 'monitoring', resource_monitor: 'monitoring',
  remote_access: 'monitoring', remote_assist: 'monitoring', quick_launch: 'monitoring', voice_calls: 'comms',
  live_assist_pro: 'comms',
  group_network: 'monitoring', group_remote: 'monitoring',
  // Communications
  chat: 'comms', email_accounts: 'comms', whatsapp: 'comms',
  comms_monitor: 'comms', inbound_rules: 'comms', telegram_routes: 'comms',
  group_comms: 'comms', feedback_requests: 'comms',
  // Finance & HR
  group_invoicing: 'finance', budgets: 'finance', hr: 'finance', customer_accounts: 'finance', supplier_accounts: 'finance', my_platforms: 'main', cloud_costs: 'finance', tool_costs: 'finance',
  my_payroll: 'finance', agent_activity: 'finance',
  costs_hub: 'finance',
  // Knowledge & Projects
  knowledge_base: 'knowledge', rag_search: 'knowledge', procedures: 'knowledge', provisioning: 'knowledge',
  group_knowledge: 'knowledge', changelog: 'knowledge', projects: 'knowledge',
  // Reports & Analytics
  reports: 'reports', diagnostics: 'reports',
  agent_status: 'reports', agent_ratings: 'reports',
  // Security & Access
  access_control: 'access', file_permissions: 'access',
  personnel: 'access', audit_log: 'access', group_security: 'access',
  // Admin
  agents: 'admin', settings: 'admin',
  agent_dashboards: 'admin', group_team: 'admin', group_admin_tools: 'admin',
};
const NAV_CATEGORY_ORDER = [
  'main', 'operations', 'inventory', 'monitoring', 'comms',
  'finance', 'knowledge', 'reports', 'access', 'admin', 'other',
];

/* ── Collapsible state hook (tri-state: explicit toggle overrides route) ──
   `openByPath` opens the group automatically when the route is inside it, BUT
   the user can still collapse/expand explicitly via the chevron — even while
   sitting on a page inside the group. The explicit override wins until the user
   navigates to a different section (openByPath changes), which re-syncs to auto.
   `toggle` is safe to attach to a chevron inside a NavLink: it stops the click
   from bubbling/navigating so the arrow only collapses/expands. */
function useCollapsible(openByPath) {
  const [override, setOverride] = useState(null);
  useEffect(() => { setOverride(null); }, [openByPath]);
  const open = override === null ? openByPath : override;
  const toggle = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setOverride((o) => !(o === null ? openByPath : o));
  }, [openByPath]);
  return [open, toggle, setOverride];
}

/* ── Collapsible sidebar group (generic; manages its own open/close state) ──
   Bundles related flat nav items under one expandable header. Opens
   automatically when the route matches any pathPrefix; the whole header (and the
   chevron) toggles on click — including collapsing while on a page inside it.
   Children are the sub-link NavLinks; each stays independently module-gated. */
function NavGroup({ icon: Icon, label, pathPrefixes = [], reorderMode, badge = 0, children }) {
  const location = useLocation();
  const openByPath = pathPrefixes.some((p) => location.pathname.startsWith(p));
  const [open, toggle] = useCollapsible(openByPath);
  return (
    <>
      <div className="nav-item" style={{ cursor: 'pointer' }} onClick={toggle}>
        {reorderMode && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
        <Icon size={16} />
        {label}
        {badge > 0 && !reorderMode && (
          <span className="nav-badge" style={{ marginLeft: 'auto' }}>{badge > 99 ? '99+' : badge}</span>
        )}
        <ChevronDown className="nav-chevron" size={12} style={{ marginLeft: badge > 0 ? 8 : 'auto', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      {open && !reorderMode && (
        <div style={{ marginLeft: 12, borderLeft: '1px solid var(--c-border)', paddingLeft: 8 }}>
          {children}
        </div>
      )}
    </>
  );
}

/* ── Module visit badge (admin only) ── */
function ModuleVisitBadge({ moduleKey }) {
  const [visitors, setVisitors] = useState(null);
  const [show, setShow] = useState(false);
  const badgeRef = useRef(null);
  const popoverRef = useRef(null);

  const fetchVisitors = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (show) { setShow(false); return; }
    api.get(`/module-visits/${moduleKey}`).then(r => {
      setVisitors(r.data);
      setShow(true);
    }).catch(() => {});
  }, [moduleKey, show]);

  // Close on click outside
  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) &&
          badgeRef.current && !badgeRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
  };

  const roleLabel = (role) => {
    if (role === 'client') return 'Client';
    if (role === 'admin') return 'Admin';
    if (role === 'manager') return 'Manager';
    return 'Agent';
  };

  const roleColor = (role) => {
    if (role === 'client') return '#f59e0b';
    if (role === 'admin') return 'var(--c-brand)';
    if (role === 'manager') return '#8b5cf6';
    return '#6b7280';
  };

  return (
    <>
      <span
        ref={badgeRef}
        className="module-visit-badge"
        onClick={fetchVisitors}
        title="Recent visitors"
      >
        <Eye size={11} />
      </span>
      {show && visitors && ReactDOM.createPortal(
        <div
          ref={popoverRef}
          className="module-visit-popover"
          style={{
            position: 'fixed',
            top: badgeRef.current ? badgeRef.current.getBoundingClientRect().top - 4 : 0,
            left: badgeRef.current ? badgeRef.current.getBoundingClientRect().right + 8 : 0,
          }}
        >
          <div className="module-visit-popover-header">
            <Eye size={12} /> Recent visitors
          </div>
          {visitors.length === 0 && (
            <div style={{ padding: '12px 14px', fontSize: 12, color: 'var(--c-text-muted)' }}>
              No visits recorded yet
            </div>
          )}
          {visitors.map((v, i) => {
            const initials = `${(v.first_name || '')[0] || ''}${(v.last_name || '')[0] || ''}`.toUpperCase();
            return (
              <div key={i} className="module-visit-row">
                {v.avatar_url
                  ? <img src={v.avatar_url} alt="" className="module-visit-avatar" />
                  : <span className="module-visit-avatar-placeholder">{initials}</span>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v.first_name} {v.last_name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
                    <span className="module-visit-role" style={{ color: roleColor(v.role), borderColor: roleColor(v.role) }}>
                      {roleLabel(v.role)}
                    </span>
                    {v.source === 'portal' && (
                      <span className="module-visit-role" style={{ color: '#0891b2', borderColor: '#0891b2' }}>Portal</span>
                    )}
                  </div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>
                  {formatTime(v.visited_at)}
                </span>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

/* ── Granular error boundaries ── */
class PageBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('[PageBoundary]', error, info.componentStack);
    reportClientError({ error, componentStack: info.componentStack, boundary: 'Page' });
  }
  componentDidUpdate(prevProps) {
    // Auto-recover when navigating to a different page
    if (this.state.hasError && this.props.locationKey !== prevProps.locationKey) {
      this.setState({ hasError: false, error: null });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', padding: 32 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>:(</div>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: 'var(--c-text)' }}>{i18n.t('errorBoundary.pageTitle')}</h2>
          <p style={{ fontSize: 13, color: 'var(--c-text-muted)', marginBottom: 16, textAlign: 'center', maxWidth: 400 }}>
            {i18n.t('errorBoundary.pageMessage')}
          </p>
          <pre style={{ fontSize: 11, color: '#ef4444', background: 'var(--c-surface-2)', padding: 12, borderRadius: 8, maxWidth: 400, overflow: 'auto', marginBottom: 16, border: '1px solid var(--c-border)' }}>
            {this.state.error?.message || 'Unknown error'}
          </pre>
          <button onClick={() => this.setState({ hasError: false, error: null })} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'var(--c-brand)', color: '#fff', fontSize: 13, fontWeight: 500 }}>
            {i18n.t('errorBoundary.retry')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

class SilentBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) {
    console.error(`[SilentBoundary:${this.props.name || 'unknown'}]`, error, info.componentStack);
    // Estos se llaman "silenciosos" para el USUARIO —el widget desaparece y la pantalla
    // sigue—, no para nosotros. Un widget roto que nadie reporta puede vivir meses.
    reportClientError({ error, componentStack: info.componentStack, boundary: `Silent:${this.props.name || '?'}` });
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

/* ── helpers ── */
function timeAgo(date) {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const PRIORITY_COLORS = { urgent: '#ef4444', high: '#f97316', normal: '#3b82f6', low: '#6b7280' };

/* ── Toast dismiss button ── */
const toastDismissStyle = {
  position: 'absolute', top: 4, right: 4, background: 'none', border: 'none',
  cursor: 'pointer', padding: 2, borderRadius: 4, color: 'var(--c-text-muted)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

/* ── Custom ticket toast ── */
function TicketToast({ t: toastInstance, ticket, onClick }) {
  const isValidation = ticket.status === 'validation';
  const borderColor = isValidation ? '#f59e0b' : (PRIORITY_COLORS[ticket.priority] || PRIORITY_COLORS.normal);
  const subject = ticket.subject?.length > 40 ? ticket.subject.slice(0, 40) + '...' : ticket.subject;
  return (
    <div
      className={`presence-toast ${toastInstance.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
      style={{ borderLeftColor: borderColor, cursor: 'pointer', position: 'relative' }}
      onClick={() => { onClick(ticket.id); toast.dismiss(toastInstance.id); }}
    >
      <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(toastInstance.id); }}><X size={14} /></button>
      <div className="presence-toast-body">
        <span className="presence-toast-name">
          {isValidation ? 'OTP' : `#${ticket.ticket_number}`}
          {ticket.company_name && (
            <span style={{
              marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '1px 6px', borderRadius: 4, fontSize: 11,
              background: ticket.label_color ? `${ticket.label_color}22` : 'var(--c-surface-2)',
              border: `1px solid ${ticket.label_color || 'var(--c-border)'}`,
              color: ticket.label_color || 'var(--c-text-muted)',
            }}>
              {ticket.company_logo_url && (
                <CompanyLogo name={ticket.company_name} logoUrl={ticket.company_logo_url} size={12} tile={false} />
              )}
              {ticket.company_name}
            </span>
          )}
        </span>
        <span className="presence-toast-status">{subject}</span>
      </div>
    </div>
  );
}

/* ── Custom errand toast ── */
const ERRAND_PRIORITY_COLORS = { urgent: '#ef4444', high: '#f59e0b', normal: '#3b82f6', low: '#6b7280' };
const ERRAND_PRIORITY_LABELS = { urgent: 'Urgente', high: 'Alta', normal: 'Normal', low: 'Baja' };
function ErrandToast({ t: toastInstance, errand, onClick }) {
  const titleText = errand.title?.length > 40 ? errand.title.slice(0, 40) + '...' : errand.title;
  const prio = errand.priority || 'normal';
  const prioColor = ERRAND_PRIORITY_COLORS[prio] || '#6b7280';
  const dueDate = errand.dueDate || errand.due_date;
  return (
    <div
      className={`presence-toast ${toastInstance.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
      style={{ borderLeftColor: prioColor, cursor: 'pointer', position: 'relative' }}
      onClick={() => { onClick(); toast.dismiss(toastInstance.id); }}
    >
      <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(toastInstance.id); }}><X size={14} /></button>
      <div className="presence-toast-body">
        <span className="presence-toast-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ClipboardList size={12} />
          #{errand.errandNumber || errand.errand_number}
          <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600, color: prioColor, background: `${prioColor}18` }}>
            {ERRAND_PRIORITY_LABELS[prio] || prio}
          </span>
        </span>
        <span className="presence-toast-status">{titleText}</span>
        {dueDate && (
          <span style={{ fontSize: 10, color: '#6b7280' }}>
            Vence: {new Date(dueDate).toLocaleDateString('es-AR')}
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Custom presence toast ── */
function PresenceToast({ t: toastInstance, agent, isOnline }) {
  const { t } = useTranslation();
  const accentColor = isOnline ? '#22c55e' : '#6b7280';
  const initials = (agent.name || '')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className={`presence-toast ${toastInstance.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
      style={{ borderLeftColor: accentColor, position: 'relative' }}
    >
      <button style={toastDismissStyle} onClick={() => toast.dismiss(toastInstance.id)}><X size={14} /></button>
      <div className="presence-toast-avatar">
        {agent.avatar_url
          ? <img src={agent.avatar_url} alt="" />
          : <span>{initials}</span>}
        <span
          className="presence-toast-dot"
          style={{ background: accentColor }}
        />
      </div>
      <div className="presence-toast-body">
        <span className="presence-toast-name">{agent.name}</span>
        <span className="presence-toast-status">
          {isOnline ? t('presence.isOnline') : t('presence.wentOffline')}
        </span>
      </div>
    </div>
  );
}


/* ── Blues Brothers Easter Egg ── */
function BluesBrothersOverlay({ onClose }) {
  const dosRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    // Load js-dos CSS
    if (!document.getElementById('jsdos-css')) {
      const link = document.createElement('link');
      link.id = 'jsdos-css';
      link.rel = 'stylesheet';
      link.href = 'https://v8.js-dos.com/latest/js-dos.css';
      document.head.appendChild(link);
    }

    // Load js-dos JS and init
    const initGame = () => {
      if (dosRef.current && window.Dos) {
        instanceRef.current = window.Dos(dosRef.current, {
          url: '/blues-brothers.jsdos',
        });
      }
    };

    if (window.Dos) {
      initGame();
    } else {
      const script = document.createElement('script');
      script.src = 'https://v8.js-dos.com/latest/js-dos.js';
      script.onload = initGame;
      document.head.appendChild(script);
    }

    return () => {
      if (instanceRef.current?.stop) instanceRef.current.stop();
    };
  }, []);

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999,
      background: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.3s ease'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 18, fontWeight: 800, color: '#fff', textShadow: '0 2px 10px rgba(59,130,246,0.5)' }}>
          🎸 The Blues Brothers (1991)
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff', borderRadius: 8, padding: '4px 12px', cursor: 'pointer',
            fontSize: 12, fontWeight: 600,
          }}
        >
          ESC ×
        </button>
      </div>
      <div
        ref={dosRef}
        style={{
          width: '80vw', maxWidth: 900, height: '70vh', maxHeight: 600,
          border: '2px solid rgba(59,130,246,0.3)', borderRadius: 12,
          overflow: 'hidden', background: '#000',
        }}
      />
      <div style={{ color: '#555', fontSize: 11, marginTop: 6 }}>
        Click the game to focus · Powered by js-dos
      </div>
    </div>
  );
}

const CONFETTI_COLORS = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd', '#01a3a4', '#f368e0', '#ff9f43', '#10ac84'];
const CONFETTI_COUNT = 150;

function BirthdayOverlay({ name, onDone }) {
  const confetti = useMemo(() =>
    Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 2,
      duration: 2 + Math.random() * 3,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      size: 6 + Math.random() * 6,
      rotation: Math.random() * 360,
      shape: i % 3, // 0=square, 1=circle, 2=strip
    })),
  []);

  useEffect(() => {
    const timer = setTimeout(onDone, 6000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999, pointerEvents: 'none', overflow: 'hidden'
    }}>
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes birthday-banner-in {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; }
          50% { transform: translate(-50%, -50%) scale(1.05); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        }
        @keyframes birthday-banner-out {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
        }
      `}</style>
      {confetti.map(c => (
        <div key={c.id} style={{
          position: 'absolute',
          left: `${c.left}%`,
          top: -20,
          width: c.shape === 2 ? c.size * 0.4 : c.size,
          height: c.shape === 2 ? c.size * 1.6 : c.size,
          background: c.color,
          borderRadius: c.shape === 1 ? '50%' : c.shape === 2 ? 2 : 1,
          animation: `confetti-fall ${c.duration}s ${c.delay}s ease-in forwards`,
          transform: `rotate(${c.rotation}deg)`,
          opacity: 0,
        }} />
      ))}
      <div style={{
        position: 'absolute', top: '40%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'var(--c-surface)',
        border: '2px solid var(--c-border)',
        borderRadius: 16,
        padding: '28px 40px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        textAlign: 'center',
        pointerEvents: 'auto',
        animation: 'birthday-banner-in 0.6s ease-out forwards, birthday-banner-out 0.5s 5s ease-in forwards',
        zIndex: 100000,
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🎂</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: 'var(--c-text)' }}>
          Happy Birthday, {name}!
        </div>
        <div style={{ fontSize: 14, color: 'var(--c-text-muted)' }}>
          Your team wishes you a wonderful day! 🎉
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Header clock ── */
function HeaderClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  const date = now.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return (
    <span className="header-clock" style={{ fontSize: 13, color: 'var(--c-text-muted)', fontWeight: 500, letterSpacing: '0.01em' }}>
      {date} <span style={{ opacity: 0.5, margin: '0 4px' }}>·</span> {time}
    </span>
  );
}

/* ── Server load indicator (admin only) ── */
function ServerLoadIndicator() {
  const [info, setInfo] = useState(null);
  const visible = usePageVisible();
  useEffect(() => {
    if (!visible) return;
    let mounted = true;
    const fetch_ = () => {
      api.get('/system-info').then(r => { if (mounted) setInfo(r.data); }).catch(() => {});
    };
    fetch_();
    const id = setInterval(fetch_, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, [visible]);

  if (!info) return null;

  const color = v => v < 60 ? '#22c55e' : v < 85 ? '#eab308' : '#ef4444';
  const formatUptime = s => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const fmtMB = b => `${(b / 1024 / 1024).toFixed(0)} MB`;

  const fmtGB = b => `${(b / 1024 / 1024 / 1024).toFixed(1)}G`;
  const diskTooltip = info.disk ? `\nDisk: ${fmtGB(info.disk.used)} / ${fmtGB(info.disk.total)} (${info.disk.percent}%) — ${fmtGB(info.disk.free)} free` : '';
  const tooltip = `CPU: ${info.cpu}%\nRAM: ${fmtMB(info.memory.used)} / ${fmtMB(info.memory.total)} (${info.memory.percent}%)\nUptime: ${formatUptime(info.uptime)}${diskTooltip}`;

  const pill = (icon, value, pct) => (
    <span className="server-load-pill" style={{ color: color(pct), borderColor: color(pct) }}>
      {icon} {value}%
    </span>
  );

  return (
    <span className="server-load-indicator" title={tooltip}>
      {pill(<Cpu size={12} />, info.cpu, info.cpu)}
      {pill(<HardDrive size={12} />, info.memory.percent, info.memory.percent)}
      {info.disk && (
        <span className="server-load-pill" style={{ color: color(info.disk.percent), borderColor: color(info.disk.percent) }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5V19A9 3 0 0 0 21 19V5"/><path d="M3 12A9 3 0 0 0 21 12"/></svg>
          {' '}{fmtGB(info.disk.free)} free
        </span>
      )}
      <span style={{ fontSize: 11, color: 'var(--c-text-muted)', opacity: 0.7 }}>{formatUptime(info.uptime)}</span>
    </span>
  );
}

/* ── Offline detection hook ── */
function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  return online;
}

/* ── Page visibility hook — pause polling when tab is hidden ── */
function usePageVisible() {
  const [visible, setVisible] = useState(!document.hidden);
  useEffect(() => {
    const handler = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);
  return visible;
}

/* ── Phone Directory Popover ── */
function PhonebookPopover({ isAdmin }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', category: '' });
  const [copied, setCopied] = useState(null);
  const ref = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/phonebook').then(r => setContacts(r.data.contacts)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const save = async () => {
    if (!form.name || !form.phone) return;
    try {
      if (editId) {
        await api.patch(`/phonebook/${editId}`, form);
      } else {
        await api.post('/phonebook', form);
      }
      setForm({ name: '', phone: '', category: '' });
      setAdding(false);
      setEditId(null);
      load();
      toast.success(t('common.saved'));
    } catch { toast.error(t('common.actionFailed')); }
  };

  const remove = async (id) => {
    if (!window.confirm(t('common.confirmDeleteContact'))) return;
    try {
      await api.delete(`/phonebook/${id}`);
      toast.success(t('common.deleted'));
    } catch { toast.error(t('common.actionFailed')); }
    load();
  };

  const startEdit = (c) => {
    setForm({ name: c.name, phone: c.phone, category: c.category || '' });
    setEditId(c.id);
    setAdding(true);
  };

  const copyPhone = (phone, id) => {
    navigator.clipboard.writeText(phone).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  // Group by category, ordered by min(sort_order) within each category.
  // Entries within a category are ordered by sort_order.
  // Reason: a single sort_order column drives both — moving a category
  // shifts every entry inside it as a block.
  const grouped = useMemo(() => {
    const groups = {};
    contacts.forEach(c => {
      const cat = c.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(c);
    });
    // Sort entries inside each category, then return as an ordered array of [cat, items]
    const entries = Object.entries(groups).map(([cat, items]) => {
      items.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      return [cat, items];
    });
    entries.sort((a, b) => (a[1][0]?.sort_order || 0) - (b[1][0]?.sort_order || 0));
    return entries;
  }, [contacts]);

  // Persist a new ordering: assign sort_order = index*10 across the flattened list,
  // then send a single bulk reorder request.
  const persistOrder = async (flat) => {
    const items = flat.map((c, i) => ({ id: c.id, sort_order: (i + 1) * 10 }));
    // Optimistic update so the popover re-renders immediately
    setContacts(items.map((it, i) => ({ ...flat[i], sort_order: it.sort_order })));
    try { await api.post('/phonebook/reorder', { items }); } catch { toast.error(t('common.actionFailed')); load(); }
  };

  const moveEntry = (catIdx, itemIdx, dir) => {
    const flat = grouped.flatMap(([, items]) => items);
    const item = grouped[catIdx][1][itemIdx];
    const target = grouped[catIdx][1][itemIdx + dir];
    if (!target) return;
    const a = flat.indexOf(item), b = flat.indexOf(target);
    [flat[a], flat[b]] = [flat[b], flat[a]];
    persistOrder(flat);
  };

  const moveCategory = (catIdx, dir) => {
    const target = grouped[catIdx + dir];
    if (!target) return;
    const reordered = [...grouped];
    [reordered[catIdx], reordered[catIdx + dir]] = [reordered[catIdx + dir], reordered[catIdx]];
    persistOrder(reordered.flatMap(([, items]) => items));
  };

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        className="notification-bell"
        onClick={() => { setOpen(!open); setAdding(false); setEditId(null); }}
        data-tooltip="Phone Directory"
      >
        <Phone size={18} />
      </button>

      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setOpen(false))(e); } }} />
          <div className="notification-dropdown" style={{ width: 320, maxWidth: 'calc(100vw - 32px)', maxHeight: 420, overflowY: 'auto' }}>
            <div className="notification-dropdown-header">
              <span style={{ fontWeight: 600, fontSize: 13 }}>Phone Directory</span>
              {isAdmin && !adding && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(true); setEditId(null); setForm({ name: '', phone: '', category: '' }); }} style={{ padding: '2px 6px' }}>
                  <Plus size={14} />
                </button>
              )}
            </div>

            {adding && isAdmin && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input className="form-input" placeholder="Name (e.g. Telecom)" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={{ fontSize: 12, padding: '6px 8px' }} autoFocus />
                <input className="form-input" placeholder="Phone (e.g. 0800-555-3569)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={{ fontSize: 12, padding: '6px 8px' }} />
                <input className="form-input" placeholder="Category (optional)" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={{ fontSize: 12, padding: '6px 8px' }} />
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-primary btn-sm" onClick={save} disabled={!form.name || !form.phone} style={{ fontSize: 11 }}>{editId ? 'Update' : 'Add'}</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setEditId(null); }} style={{ fontSize: 11 }}>Cancel</button>
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>Loading...</div>
            ) : contacts.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--c-text-muted)', fontSize: 13 }}>No contacts yet.</div>
            ) : (
              grouped.map(([cat, items], catIdx) => (
                <div key={cat}>
                  {grouped.length > 1 && (
                    <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--c-text-muted)', background: 'var(--c-bg)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ flex: 1 }}>{cat}</span>
                      {isAdmin && (
                        <>
                          <button className="btn btn-ghost btn-sm" disabled={catIdx === 0} onClick={() => moveCategory(catIdx, -1)} style={{ padding: '0 4px', opacity: catIdx === 0 ? 0.3 : 1 }} data-tooltip="Move category up"><ChevronUp size={12} /></button>
                          <button className="btn btn-ghost btn-sm" disabled={catIdx === grouped.length - 1} onClick={() => moveCategory(catIdx, 1)} style={{ padding: '0 4px', opacity: catIdx === grouped.length - 1 ? 0.3 : 1 }} data-tooltip="Move category down"><ChevronDown size={12} /></button>
                        </>
                      )}
                    </div>
                  )}
                  {items.map((c, itemIdx) => (
                    <div key={c.id} style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text)' }}>{c.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--c-text-muted)', fontFamily: 'monospace' }}>{c.phone}</div>
                      </div>
                      {isAdmin && (
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <button className="btn btn-ghost btn-sm" disabled={itemIdx === 0} onClick={() => moveEntry(catIdx, itemIdx, -1)} style={{ padding: 0, lineHeight: 1, opacity: itemIdx === 0 ? 0.3 : 1 }} data-tooltip="Move up"><ChevronUp size={12} /></button>
                          <button className="btn btn-ghost btn-sm" disabled={itemIdx === items.length - 1} onClick={() => moveEntry(catIdx, itemIdx, 1)} style={{ padding: 0, lineHeight: 1, opacity: itemIdx === items.length - 1 ? 0.3 : 1 }} data-tooltip="Move down"><ChevronDown size={12} /></button>
                        </div>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => copyPhone(c.phone, c.id)}
                        style={{ padding: '2px 6px', color: copied === c.id ? 'var(--c-success)' : 'var(--c-text-muted)' }}
                        data-tooltip="Copy"
                      >
                        {copied === c.id ? <Check size={13} /> : <Copy size={13} />}
                      </button>
                      {isAdmin && (
                        <>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(c)} style={{ padding: '2px 6px' }}><Pencil size={13} /></button>
                          <button className="btn btn-ghost btn-sm" onClick={() => remove(c.id)} style={{ padding: '2px 6px', color: 'var(--c-danger)' }}><X size={13} /></button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout, isAdmin, isDemo, isManager, isScoped, isClient, hasModule, updateLanguage, theme, toggleTheme, isImpersonating, stopImpersonation } = useAuth();
  const isOnline = useOnlineStatus();
  const isPageVisible = usePageVisible();
  const {
    callState, incomingCall,
    acceptCall, rejectCall, endCall,
    toggleVideo, toggleAudio,
    conversations: callConversations,
  } = useCallContext();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketsOpenByPath = location.pathname.startsWith('/tickets') || location.pathname.startsWith('/my-tickets') || location.pathname.startsWith('/trash') || location.pathname.startsWith('/spam') || location.pathname.startsWith('/papelera') || location.pathname.startsWith('/validation') || location.pathname.startsWith('/notifications') || location.pathname.startsWith('/tickets-private');

  // Auto-show "Servidor de Archivos" in the sidebar when the user's company has any
  // file_share data, even if the module isn't explicitly enabled on their account.
  // Reason: clients with personal-folder data should see the module appear without
  // needing per-agent toggling. Cheap one-shot probe — cached for the session.
  const [fileServerHasData, setFileServerHasData] = useState(() => {
    try { return sessionStorage.getItem('fp_has_data') === '1'; } catch { return false; }
  });
  useEffect(() => {
    if (isClient) return; // portal users use a different layout
    // Sin el módulo, todo /file-permissions responde 403 (el router entero está
    // gateado). Preguntar igual generaba un 403 por CADA carga de página de CADA
    // agente sin el módulo: la mayor fuente de ruido del panel de fricción, y
    // encima atribuido a la pantalla donde el agente estuviera parado, no acá.
    // La respuesta a "¿tengo datos?" cuando no tengo el módulo ya la sabemos.
    if (!hasModule('file_permissions')) { setFileServerHasData(false); return; }
    let cancelled = false;
    import('../../utils/api').then(({ default: api }) => {
      api.get('/file-permissions/has-data')
        .then(r => {
          if (cancelled) return;
          const v = !!r.data?.has_data;
          setFileServerHasData(v);
          try { sessionStorage.setItem('fp_has_data', v ? '1' : '0'); } catch {}
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [isClient, hasModule]);
  const [ticketsOpen, toggleTickets, setTicketsOpen] = useCollapsible(ticketsOpenByPath);
  const suppliesOpenByPath = location.pathname.startsWith('/supplies');
  const [suppliesOpen, toggleSupplies] = useCollapsible(suppliesOpenByPath);
  const healthOpenByPath = location.pathname.startsWith('/backup-monitor') || location.pathname.startsWith('/ops-monitor') || location.pathname.startsWith('/vps-health') || location.pathname.startsWith('/dmarc') || location.pathname.startsWith('/ip-intelligence');
  const [healthOpen, toggleHealth] = useCollapsible(healthOpenByPath);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Barra lateral plegada a íconos. Se guarda por usuario en el navegador: es una
  // preferencia de cómo trabajás, no un estado de la app, y tiene que sobrevivir
  // al reload igual que el tema o el orden del menú.
  // Marca de la instancia (white-label). Arranca con lo cacheado para que no parpadee
  // y se refresca contra el servidor.
  const [marca, setMarca] = useState(getBrand());
  const [anchoLogo, setAnchoLogo] = useState(1);   // proporción real del logo, medida al cargarlo
  useEffect(() => { cargarBrand().then(setMarca); }, []);
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('nav_collapsed') === '1'; } catch { return false; }
  });
  const toggleNavCollapsed = useCallback(() => {
    setNavCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('nav_collapsed', next ? '1' : '0'); } catch { /* modo privado */ }
      track('ui', next ? 'nav_collapse' : 'nav_expand', null, { result: 'ok' });
      return next;
    });
  }, []);
  const [showKbHelp, setShowKbHelp] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showQuickBug, setShowQuickBug] = useState(false);
  const isMobile = useIsMobile();

  // Global console-error buffer for the Quick Bug modal
  useConsoleErrorBuffer();

  // Detect soft keyboard open on mobile and toggle body.keyboard-open
  // (used by CSS to hide bottom nav + FAB so they don't cover the input)
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      const heightDiff = window.innerHeight - vv.height;
      document.body.classList.toggle('keyboard-open', heightDiff > 150);
    };
    vv.addEventListener('resize', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      document.body.classList.remove('keyboard-open');
    };
  }, [isMobile]);

  // Global Cmd+K / Ctrl+K keyboard shortcut for command palette
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Global Ctrl+Shift+B quick-bug reporter (skip if typing in an input)
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        const target = e.target;
        const tag = target?.tagName;
        const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable;
        if (isEditable) return;
        e.preventDefault();
        setShowQuickBug(prev => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Global ? keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if (showKbHelp) return; // let modal's own Escape handler work
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.activeElement?.classList?.contains('ql-editor')) return;
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setShowKbHelp(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showKbHelp]);

  // ── Nav reorder (admin only) ──
  const [reorderMode, setReorderMode] = useState(false);
  const [navOrder, setNavOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav_order')); } catch { return null; }
  });
  const dragItemIdx = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // ── Sidebar view mode (default | custom | categories | alphabetical) ──
  const [navViewMode, setNavViewModeState] = useState(() => {
    const saved = localStorage.getItem('nav_view_mode');
    return NAV_VIEW_MODES.includes(saved) ? saved : 'default';
  });
  const setNavViewMode = useCallback((mode) => {
    if (!NAV_VIEW_MODES.includes(mode)) return;
    setNavViewModeState(mode);
    localStorage.setItem('nav_view_mode', mode);
  }, []);
  const [collapsedCats, setCollapsedCats] = useState(() => {
    try {
      const arr = JSON.parse(localStorage.getItem('nav_collapsed_cats')) || [];
      return new Set(arr);
    } catch { return new Set(); }
  });
  const toggleCollapsedCat = useCallback((cat) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      localStorage.setItem('nav_collapsed_cats', JSON.stringify([...next]));
      return next;
    });
  }, []);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const viewMenuRef = useRef(null);
  useEffect(() => {
    if (!showViewMenu) return;
    const onClick = (e) => {
      if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) {
        setShowViewMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showViewMenu]);

  useEffect(() => {
    if (!reorderMode) return;
    const onKey = (e) => { if (e.key === 'Escape') setReorderMode(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reorderMode]);

  const handleNavDrop = useCallback((orderedKeys, dropIdx) => {
    const fromIdx = dragItemIdx.current;
    if (fromIdx === null || fromIdx === dropIdx) {
      dragItemIdx.current = null;
      setDragOverIdx(null);
      return;
    }
    const newKeys = [...orderedKeys];
    const [moved] = newKeys.splice(fromIdx, 1);
    newKeys.splice(dropIdx, 0, moved);
    setNavOrder(newKeys);
    localStorage.setItem('nav_order', JSON.stringify(newKeys));
    dragItemIdx.current = null;
    setDragOverIdx(null);
  }, []);

  const handleNavDragEnd = useCallback(() => {
    dragItemIdx.current = null;
    setDragOverIdx(null);
  }, []);

  const resetNavOrder = useCallback(() => {
    setNavOrder(null);
    localStorage.removeItem('nav_order');
    setReorderMode(false);
    setNavViewModeState('default');
    localStorage.setItem('nav_view_mode', 'default');
  }, []);

  // (force-open reset is handled inside useCollapsible)

  // Impersonation event listeners
  useEffect(() => {
    const onBlocked = () => toast.error(t('impersonation.blocked'));
    const onExpired = () => {
      stopImpersonation();
      toast(t('impersonation.expired'), { icon: '👁' });
    };
    window.addEventListener('impersonation-blocked', onBlocked);
    window.addEventListener('impersonation-expired', onExpired);
    return () => {
      window.removeEventListener('impersonation-blocked', onBlocked);
      window.removeEventListener('impersonation-expired', onExpired);
    };
  }, [t, stopImpersonation]);

  // Validation ticket count polling — tracks seen IDs in localStorage
  const [validationCount, setValidationCount] = useState(0);
  const [validationShine, setValidationShine] = useState(false);
  const prevUnseenCount = useRef(null);
  const otpSeenRef = useRef(new Set());
  const otpFetchedIdsRef = useRef([]);

  // Initialise seen set once
  useEffect(() => {
    try { otpSeenRef.current = new Set(JSON.parse(localStorage.getItem('otp_seen_ids') || '[]')); } catch { otpSeenRef.current = new Set(); }
  }, []);

  const markOtpSeen = useCallback(() => {
    const ids = otpFetchedIdsRef.current;
    ids.forEach(id => otpSeenRef.current.add(id));
    localStorage.setItem('otp_seen_ids', JSON.stringify([...otpSeenRef.current]));
    setValidationCount(0);
  }, []);

  useEffect(() => {
    if (!isPageVisible) return;
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/tickets', { params: { status: 'validation', limit: 200 } });
        const fetchedIds = (data.tickets || []).map(t => t.id);
        otpFetchedIdsRef.current = fetchedIds;

        // Prune seen IDs to only those still present
        const fetchedSet = new Set(fetchedIds);
        const pruned = new Set([...otpSeenRef.current].filter(id => fetchedSet.has(id)));
        otpSeenRef.current = pruned;
        localStorage.setItem('otp_seen_ids', JSON.stringify([...pruned]));

        const unseenCount = fetchedIds.filter(id => !pruned.has(id)).length;
        if (prevUnseenCount.current !== null && unseenCount > prevUnseenCount.current) {
          setValidationShine(true);
          if (!ticketsOpenByPath) setTicketsOpen(true);
          setTimeout(() => setValidationShine(false), 6000);
        }
        prevUnseenCount.current = unseenCount;
        setValidationCount(unseenCount);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 45000);
    return () => clearInterval(interval);
  }, [ticketsOpenByPath, isPageVisible]);
  // Auto-clear validation badge when navigating to /validation (via any route)
  useEffect(() => {
    if (location.pathname === '/validation') markOtpSeen();
    if (location.pathname === '/field-tasks') {
      setErrandUnreadCount(0);
      localStorage.setItem('errand_unread_count', '0');
    }
  }, [location.pathname, markOtpSeen]);

  const [showUserMenu, setShowUserMenu] = useState(false);

  // ── Version pill setting (fetched once) ──
  const [showVersionPill, setShowVersionPill] = useState(false);
  useEffect(() => {
    if (!user) return;
    api.get('/settings/public').then(r => {
      setShowVersionPill(r.data.settings?.show_version_pill === 'true');
    }).catch(() => {});
  }, [user?.id]); // eslint-disable-line

  // ── Chat: from shared context (single socket) ──
  const { totalUnread: chatUnread, chatNotifications, clearChatNotifications, openFloatingConversation, socketRef: chatSocketRef } = useChatContext();

  // ── Changelog: unseen-count badge ──
  // Polls cheaply on mount + when navigating away from /changelog (so the badge
  // clears after the user reads them).
  const [changelogUnseen, setChangelogUnseen] = useState(0);
  useEffect(() => {
    if (!user || isClient) return;
    // /feedback está gateado por el módulo feedback_requests. Sin él la campana
    // pedía el conteo igual y cobraba un 403 en cada cambio de ruta — no una vez
    // por sesión, una por navegación.
    if (!hasModule('feedback_requests')) return;
    let cancelled = false;
    const fetchUnseen = () => api.get('/feedback/_/unseen-count')
      .then(r => { if (!cancelled) setChangelogUnseen(r.data?.count || 0); })
      .catch(() => {});
    fetchUnseen();
    return () => { cancelled = true; };
  }, [user?.id, location.pathname, hasModule]); // eslint-disable-line

  // Konami code — works globally regardless of focus
  const [showAndy, setShowAndy] = useState(false);
  const konamiRef = useRef([]);
  useEffect(() => {
    const KONAMI = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
    const onKey = (e) => {
      konamiRef.current.push(e.key);
      konamiRef.current = konamiRef.current.slice(-KONAMI.length);
      if (konamiRef.current.length === KONAMI.length && konamiRef.current.every((k, i) => k === KONAMI[i])) {
        setShowAndy(true);
        konamiRef.current = [];
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Blues Brothers easter egg — 7 clicks on logo within 2s
  const [showBlues, setShowBlues] = useState(false);
  const logoClicksRef = useRef([]);
  useEffect(() => {
    if (!showBlues) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowBlues(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showBlues]);
  const handleLogoClick = useCallback((e) => {
    const now = Date.now();
    logoClicksRef.current.push(now);
    logoClicksRef.current = logoClicksRef.current.filter(t => now - t < 2000);
    if (logoClicksRef.current.length >= 7) {
      e.preventDefault();
      e.stopPropagation();
      logoClicksRef.current = [];
      setShowBlues(true);
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Birthday check — reactive to user changes (login vs /me refresh)
  const [showBirthday, setShowBirthday] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  // needsOnboarding: undefined = not checked yet, true/false once /status resolves.
  const [needsOnboarding, setNeedsOnboarding] = useState(undefined);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showBienvenida, setShowBienvenida] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // New-agent first-login wizard (full onboarding). Pre-existing agents are
  // backfilled as onboarded server-side, so this only fires for genuinely new users.
  useEffect(() => {
    // El onboarding de RRHH (datos personales, DNI, firma de documentos) es para empleados
    // NUESTROS. En la instancia de un cliente no aplica: sus usuarios no son personal nuestro.
    if (!user || isDemo || user.isClient || !marca.esAyudaTecno) { setNeedsOnboarding(false); return; }
    let cancelled = false;
    api.get('/agent-onboarding/status')
      .then((r) => {
        if (cancelled) return;
        const onboarded = !!r.data?.user?.onboardedAt;
        setNeedsOnboarding(!onboarded);
        if (!onboarded) setTimeout(() => setShowOnboarding(true), 1200);
      })
      .catch(() => { if (!cancelled) setNeedsOnboarding(false); });
    return () => { cancelled = true; };
  }, [user, isDemo]);

  // Primer ingreso en la instancia de un cliente: un recorrido corto para que puedan tomar
  // su primer pedido sin preguntarle a nadie. Una sola vez por usuario.
  useEffect(() => {
    // Depende de `marca`, no sólo de `user`: cargarBrand() resuelve en un efecto, así que en
    // el primer render la marca todavía es la nuestra por defecto. Evaluar ahí daría siempre
    // "es AyudaTecno" y el wizard no se mostraría nunca en un navegador sin cache.
    if (!user || user.isClient || marca.esAyudaTecno !== false) return;
    if (debeVerBienvenida(user.id)) setTimeout(() => setShowBienvenida(true), 900);
  }, [user, marca]);

  useEffect(() => {
    // The legacy profile-completion nag only fires once we know the new agent
    // onboarding wizard is NOT going to show (it supersedes it for new hires).
    // Si el usuario ya lo pospuso, no se le vuelve a plantar en la cara: es un recordatorio,
    // no un peaje. La marca es por usuario (mismo criterio que el modal del portal).
    const skipped = user && localStorage.getItem(`profile_completion_skipped_v1_${user.id}`);
    if (user && user.profileComplete === false && !skipped && !isDemo && !user.isClient && marca.esAyudaTecno && needsOnboarding === false) {
      const timer = setTimeout(() => setShowProfileCompletion(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user, isDemo, needsOnboarding]);
  useEffect(() => {
    if (!user?.birthDate) return;
    const today = new Date();
    // birth_date comes as "YYYY-MM-DD" or full ISO — extract just the date part
    const bdStr = user.birthDate.split('T')[0];
    const [, month, day] = bdStr.split('-').map(Number);
    if (today.getMonth() + 1 === month && today.getDate() === day) {
      const key = `birthday-shown-${today.getFullYear()}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        setShowBirthday(true);
      }
    }
  }, [user?.birthDate]);

  // Notification center state — persisted via localStorage
  const [showNotifications, setShowNotifications] = useState(false);
  // Vuelve a "Todo" cada vez que se cierra el panel: nadie se acuerda de que dejó un
  // filtro puesto ayer, y una campana que esconde avisos en silencio es peor que nada.
  const [notifFilter, setNotifFilter] = useState('all');
  const [notifications, setNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem('presence_notifications')) || []; } catch { return []; }
  });
  const [unreadCount, setUnreadCount] = useState(() => {
    return parseInt(localStorage.getItem('presence_unread_count'), 10) || 0;
  });

  const addNotification = useCallback((agent, isOnline) => {
    const item = {
      id: Date.now() + Math.random(),
      agent,
      isOnline,
      time: new Date().toISOString(),
    };
    setNotifications(prev => {
      const next = [item, ...prev].slice(0, 20);
      localStorage.setItem('presence_notifications', JSON.stringify(next));
      return next;
    });
    setUnreadCount(c => {
      const next = c + 1;
      localStorage.setItem('presence_unread_count', String(next));
      return next;
    });
  }, []);

  const markAllRead = () => {
    setUnreadCount(0);
    localStorage.setItem('presence_unread_count', '0');
  };

  // Notification sound preference
  // Un sonido por tipo de aviso. El estado sólo existe para que la UI se refresque al
  // elegir; la fuente de verdad es notificationSounds (localStorage), que es lo que leen
  // los que reproducen.
  const [soundMap, setSoundMap] = useState(() =>
    Object.fromEntries(SOUND_EVENTS.map(ev => [ev.key, getSoundFor(ev.key)])));
  const allSoundsOff = SOUND_EVENTS.every(ev => (soundMap[ev.key] ?? getSoundFor(ev.key)) === 'none');
  const [showSoundPicker, setShowSoundPicker] = useState(false);

  // OS-level desktop notifications (Web Notification API). State is derived from
  // localStorage + Notification.permission, refreshed on focus/visibility changes
  // so changing the permission via browser settings reflects immediately in the UI.
  const [osNotifTick, setOsNotifTick] = useState(0); // bump to force re-read
  const osNotifSupported = isOsNotifSupported();
  const osNotifPermission = osNotifSupported ? getOsNotifPermission() : 'unsupported';
  const osNotifOn = osNotifSupported && isOsNotifEnabled();
  const osNotifOnlyHidden = isOsNotifOnlyWhenHidden();
  // eslint-disable-next-line no-unused-vars
  const _osTick = osNotifTick; // dep just to rerender on tick
  useEffect(() => {
    const refresh = () => setOsNotifTick(t => t + 1);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  const [showOsNotifPicker, setShowOsNotifPicker] = useState(false);
  const handleEnableOsNotifs = async () => {
    const result = await requestOsNotifPermission();
    setOsNotifTick(t => t + 1);
    if (result === 'granted') {
      // Confirmation banner via the native channel — proves it works
      tryOsNotify({ title: `${marca.name} ${marca.tagline || ''}`.trim(), body: 'Notificaciones de escritorio activadas ✓', tag: 'os-notif-enabled' });
      // Subscribe to web push too — gets notifications when the browser is closed,
      // and is the ONLY path to OS notifications on iPhone PWAs (iOS 16.4+).
      // Silent failure is fine — desktop banners still work via the in-app socket.
      if (isWebPushSupported()) {
        subscribeWebPush().then(r => {
          if (!r.ok && r.reason !== 'no-vapid' && r.reason !== 'unsupported') {
            console.warn('[WEB-PUSH] subscribe non-fatal:', r.reason);
          }
        });
      }
    } else if (result === 'denied') {
      toast.error(t('notifications.webPushBlocked'));
    } else if (result === 'unsupported') {
      toast.error(t('notifications.webPushUnsupported'));
    }
  };

  // Ticket notification state — persisted via localStorage
  const [ticketNotifications, setTicketNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ticket_notifications')) || []; } catch { return []; }
  });
  const [ticketUnreadCount, setTicketUnreadCount] = useState(() => {
    return parseInt(localStorage.getItem('ticket_unread_count'), 10) || 0;
  });
  const seenTicketIdsRef = useRef(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('ticket_notif_seen_ids'));
      return new Set(Array.isArray(stored) ? stored : []);
    } catch { return new Set(); }
  });
  // Initialize ref lazily
  if (typeof seenTicketIdsRef.current === 'function') {
    seenTicketIdsRef.current = seenTicketIdsRef.current();
  }

  const persistSeenIds = useCallback((set) => {
    const arr = [...set].slice(-200);
    localStorage.setItem('ticket_notif_seen_ids', JSON.stringify(arr));
  }, []);

  // Errand notification state — persisted via localStorage
  const [errandNotifications, setErrandNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem('errand_notifications')) || []; } catch { return []; }
  });
  const [errandUnreadCount, setErrandUnreadCount] = useState(() => {
    return parseInt(localStorage.getItem('errand_unread_count'), 10) || 0;
  });
  const seenErrandIdsRef = useRef(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('errand_notif_seen_ids'));
      return new Set(Array.isArray(stored) ? stored : []);
    } catch { return new Set(); }
  });
  if (typeof seenErrandIdsRef.current === 'function') {
    seenErrandIdsRef.current = seenErrandIdsRef.current();
  }
  const persistSeenErrandIds = useCallback((set) => {
    const arr = [...set].slice(-200);
    localStorage.setItem('errand_notif_seen_ids', JSON.stringify(arr));
  }, []);

  // Tickets-needing-attention badge: persistent count + IDs of non-WA tickets where
  // the customer is waiting on the current user. Mirrors the WhatsApp bubble's role
  // for the email/sms/etc. queue — Andy's complaint was that agents miss email
  // replies because there's no equivalent ambient indicator. The /tickets/notifications
  // poll above only catches tickets created in the last 45 min (the bell feed); this
  // catches customer replies on long-lived tickets too.
  const [attentionCount, setAttentionCount] = useState(() => {
    return parseInt(localStorage.getItem('tickets_attention_count'), 10) || 0;
  });
  const [attentionShine, setAttentionShine] = useState(false);
  const seenAttentionIdsRef = useRef(null);
  if (seenAttentionIdsRef.current === null) {
    try {
      const stored = JSON.parse(localStorage.getItem('tickets_attention_seen_ids'));
      seenAttentionIdsRef.current = new Set(Array.isArray(stored) ? stored : []);
    } catch { seenAttentionIdsRef.current = new Set(); }
  }
  const isFirstAttentionPoll = useRef(true);

  // Notificaciones badge: count of ACTIVE monitor/probe auto-tickets (kind='notification').
  // These are hidden from the main queue by design, so the badge is the only ambient cue
  // that an outage ticket is sitting unhandled. No sound/desktop-notif here — the monitor
  // already alerts loudly via the AlertTicker + Telegram; this is just a quiet "N pending"
  // counter that stays lit until the incident is resolved. Shines when a new one appears.
  const [notificationsCount, setNotificationsCount] = useState(() => {
    return parseInt(localStorage.getItem('tickets_notification_count'), 10) || 0;
  });
  const [notificationsShine, setNotificationsShine] = useState(false);
  const seenNotificationIdsRef = useRef(null);
  if (seenNotificationIdsRef.current === null) {
    try {
      const stored = JSON.parse(localStorage.getItem('tickets_notification_seen_ids'));
      seenNotificationIdsRef.current = new Set(Array.isArray(stored) ? stored : []);
    } catch { seenNotificationIdsRef.current = new Set(); }
  }
  const isFirstNotificationPoll = useRef(true);

  // Ask once for browser notification permission so the attention poll above can
  // fire desktop notifications on customer replies. Gated on a localStorage flag
  // so we don't nag users who declined — they can re-enable via browser settings.
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'default') return; // already decided
      if (localStorage.getItem('browser_notif_asked') === '1') return;
      // Defer 5s after mount so we don't pop the prompt mid-login.
      const timer = setTimeout(() => {
        try {
          Notification.requestPermission().catch(() => {});
          localStorage.setItem('browser_notif_asked', '1');
        } catch {}
      }, 5000);
      return () => clearTimeout(timer);
    } catch {}
  }, []);

  // Ticket notification polling (15s, all roles)
  // First poll is silent — just seeds the seen set without toasts/sounds
  const isFirstTicketPoll = useRef(true);
  useEffect(() => {
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/tickets/notifications');
        const seen = seenTicketIdsRef.current;
        const newTickets = data.filter(tk => !seen.has(tk.id));

        if (newTickets.length > 0) {
          // Add to seen set
          for (const tk of newTickets) seen.add(tk.id);
          // Cap at 200
          if (seen.size > 200) {
            const arr = [...seen];
            seenTicketIdsRef.current = new Set(arr.slice(-200));
          }
          persistSeenIds(seenTicketIdsRef.current);

          // First poll: seed silently — no toasts, no sound, no unread bump
          if (isFirstTicketPoll.current) {
            isFirstTicketPoll.current = false;
            return;
          }

          // Prepend to notifications
          setTicketNotifications(prev => {
            const next = [...newTickets.map(tk => ({ ...tk, _notifTime: new Date().toISOString() })), ...prev].slice(0, 30);
            localStorage.setItem('ticket_notifications', JSON.stringify(next));
            return next;
          });

          // Increment unread
          setTicketUnreadCount(c => {
            const next = c + newTickets.length;
            localStorage.setItem('ticket_unread_count', String(next));
            return next;
          });

          // Un ticket que entró por WhatsApp ya lo anuncia la burbuja de WhatsApp
          // (su chirp + su toast verde). Acá se lo deja en la campana y en el
          // contador, pero NO se lo vuelve a anunciar: un solo WhatsApp entrante
          // sonaba dos veces, mostraba dos toasts y además un banner del sistema
          // (Santi, 17-sep-2026: "se disparan varios mensajes de notificación").
          // Si el usuario no tiene el módulo de WhatsApp, la burbuja no existe y
          // entonces sí se anuncia desde acá como cualquier otro ticket.
          const loud = hasModule('whatsapp') ? newTickets.filter(tk => tk.channel !== 'whatsapp') : newTickets;
          if (loud.length === 0) return;

          // Play notification sound
          playFor('ticket');

          // OS desktop banner — one per ticket up to 3, otherwise a single summary
          if (loud.length <= 3) {
            for (const tk of loud) {
              tryOsNotify({
                title: `🎫 #${tk.ticket_number || ''} ${tk.subject || ''}`.trim(),
                body: `${tk.contact_name || tk.contact_email || ''}${tk.company_name ? ` · ${tk.company_name}` : ''}`,
                link: `/tickets/${tk.id}`,
                tag: `ticket-${tk.id}`,
              });
            }
          } else {
            tryOsNotify({
              title: t('notifications.tickets'),
              body: t('notifications.moreTickets', { count: loud.length }),
              link: '/tickets',
              tag: 'ticket-summary',
            });
          }

          // Toast for first 3 new tickets
          if (loud.length <= 3) {
            for (const tk of loud) {
              toast.custom((ti) => <TicketToast t={ti} ticket={tk} onClick={(id) => navigate(`/tickets/${id}`)} />, { duration: 5000 });
            }
          } else {
            // Summary toast
            toast.custom((ti) => (
              <div className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`} style={{ borderLeftColor: '#3b82f6', position: 'relative' }}>
                <button style={toastDismissStyle} onClick={() => toast.dismiss(ti.id)}><X size={14} /></button>
                <div className="presence-toast-body">
                  <span className="presence-toast-name">{t('notifications.tickets')}</span>
                  <span className="presence-toast-status">{t('notifications.moreTickets', { count: loud.length })}</span>
                </div>
              </div>
            ), { duration: 5000 });
          }
        } else {
          // Even if no new tickets, mark first poll as done
          isFirstTicketPoll.current = false;
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [navigate, t, persistSeenIds, isPageVisible, hasModule]);

  // Tickets-needing-attention polling (60s) — drives the sidebar "Tickets" badge
  // and fires sound/desktop-notification when a ticket newly enters the set
  // (i.e. customer wrote on an existing ticket the user is responsible for).
  // Independent from /tickets/notifications above which only catches brand-new
  // tickets in the last 45min — this catches the long-tail "agent missed a reply"
  // case that triggered Andy's complaint about email tickets being ignored.
  useEffect(() => {
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/tickets/attention-count');
        const ids = Array.isArray(data?.ticket_ids) ? data.ticket_ids : [];
        const count = data?.count || 0;
        const seen = seenAttentionIdsRef.current;
        const newIds = ids.filter(id => !seen.has(id));

        // Prune seen set to ONLY tickets that are still in the attention set,
        // then re-add the current set. This way a ticket that left the set
        // (agent replied) and later re-enters (customer replied again) counts
        // as new — same pattern used by the validationCount poll above.
        const idsSet = new Set(ids);
        const pruned = new Set([...seen].filter(id => idsSet.has(id)));
        for (const id of ids) pruned.add(id);
        seenAttentionIdsRef.current = pruned;
        try { localStorage.setItem('tickets_attention_seen_ids', JSON.stringify([...pruned].slice(-200))); } catch {}

        // Update count + persist
        setAttentionCount(count);
        try { localStorage.setItem('tickets_attention_count', String(count)); } catch {}

        // First poll seeds silently — same pattern as ticket-notifications poll above
        if (isFirstAttentionPoll.current) {
          isFirstAttentionPoll.current = false;
          return;
        }

        // New tickets entered the attention set → notify
        if (newIds.length > 0) {
          setAttentionShine(true);
          setTimeout(() => setAttentionShine(false), 6000);

          // Sonido propio: "un cliente está esperando respuesta" no es lo mismo que
          // "entró un ticket", y distinguirlos era el reclamo de Ariel y Santi.
          playFor('ticketAttention');

          // Browser desktop notification (only if user granted permission)
          // Tag dedupes if multiple replies on same ticket fire close together.
          if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
            try {
              const body = newIds.length === 1
                ? t('notifications.ticketCustomerReplyOne')
                : t('notifications.ticketCustomerReplyMany', { count: newIds.length });
              const notif = new Notification(t('notifications.ticketCustomerReplyTitle'), {
                body,
                tag: 'tickets-attention',
                icon: '/favicon.ico',
              });
              notif.onclick = () => {
                window.focus();
                navigate(newIds.length === 1 ? `/tickets/${newIds[0]}` : '/tickets');
                notif.close();
              };
            } catch {}
          }

          // Toast — single ticket toast if just one, summary if more
          if (newIds.length === 1) {
            toast.custom((ti) => (
              <div
                className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
                style={{ borderLeftColor: '#10b981', cursor: 'pointer', position: 'relative' }}
                onClick={() => { navigate(`/tickets/${newIds[0]}`); toast.dismiss(ti.id); }}
              >
                <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
                <div className="presence-toast-body">
                  <span className="presence-toast-name">{t('notifications.ticketCustomerReplyTitle')}</span>
                  <span className="presence-toast-status">{t('notifications.ticketCustomerReplyOne')}</span>
                </div>
              </div>
            ), { duration: 5000 });
          } else {
            toast.custom((ti) => (
              <div
                className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
                style={{ borderLeftColor: '#10b981', cursor: 'pointer', position: 'relative' }}
                onClick={() => { navigate('/tickets'); toast.dismiss(ti.id); }}
              >
                <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
                <div className="presence-toast-body">
                  <span className="presence-toast-name">{t('notifications.ticketCustomerReplyTitle')}</span>
                  <span className="presence-toast-status">{t('notifications.ticketCustomerReplyMany', { count: newIds.length })}</span>
                </div>
              </div>
            ), { duration: 5000 });
          }
        }
      } catch {}

      // Notificaciones badge (active monitor/probe auto-tickets). Piggybacks on the
      // same 60s cycle. Quiet: updates the count + shines on a genuinely new one, but
      // no sound/toast/desktop-notif (the monitor already alerts via ticker + Telegram).
      try {
        const { data } = await api.get('/tickets/notification-count');
        const nIds = Array.isArray(data?.ticket_ids) ? data.ticket_ids : [];
        const nCount = data?.count || 0;
        const nSeen = seenNotificationIdsRef.current;
        const nNew = nIds.filter(id => !nSeen.has(id));

        const nIdsSet = new Set(nIds);
        const nPruned = new Set([...nSeen].filter(id => nIdsSet.has(id)));
        for (const id of nIds) nPruned.add(id);
        seenNotificationIdsRef.current = nPruned;
        try { localStorage.setItem('tickets_notification_seen_ids', JSON.stringify([...nPruned].slice(-200))); } catch {}

        setNotificationsCount(nCount);
        try { localStorage.setItem('tickets_notification_count', String(nCount)); } catch {}

        if (isFirstNotificationPoll.current) {
          isFirstNotificationPoll.current = false;
        } else if (nNew.length > 0) {
          setNotificationsShine(true);
          setTimeout(() => setNotificationsShine(false), 6000);
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 60000);
    // Igual que la burbuja: si alguien manda un ticket a la papelera el contador tiene
    // que bajar ya, no dentro de un minuto. El aviso lo emite la invalidación de
    // react-query (utils/queryClient.js), así que cubre cualquier acción sobre tickets.
    const off = onTicketsChanged(poll);
    return () => { clearInterval(interval); off(); };
  }, [navigate, t, isPageVisible]);

  // Errand notification polling (15s, all roles with errands module)
  const isFirstErrandPoll = useRef(true);
  useEffect(() => {
    if (!isPageVisible || !hasModule('errands')) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/field-tasks/notifications');
        const seen = seenErrandIdsRef.current;
        const newErrands = data.filter(e => !seen.has(e.id));

        if (newErrands.length > 0) {
          for (const e of newErrands) seen.add(e.id);
          if (seen.size > 200) {
            const arr = [...seen];
            seenErrandIdsRef.current = new Set(arr.slice(-200));
          }
          persistSeenErrandIds(seenErrandIdsRef.current);

          if (isFirstErrandPoll.current) {
            isFirstErrandPoll.current = false;
            return;
          }

          setErrandNotifications(prev => {
            const next = [...newErrands.map(e => ({ ...e, _notifTime: new Date().toISOString() })), ...prev].slice(0, 30);
            localStorage.setItem('errand_notifications', JSON.stringify(next));
            return next;
          });

          setErrandUnreadCount(c => {
            const next = c + newErrands.length;
            localStorage.setItem('errand_unread_count', String(next));
            return next;
          });

          playFor('errand');

          for (const e of newErrands.slice(0, 3)) {
            toast.custom((ti) => <ErrandToast t={ti} errand={e} onClick={() => navigate('/field-tasks')} />, { duration: 5000 });
          }
        } else {
          isFirstErrandPoll.current = false;
        }
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, [navigate, persistSeenErrandIds, isPageVisible, hasModule]);

  // Listen for errand socket events (real-time bell + toast)
  useEffect(() => {
    const s = chatSocketRef?.current;
    if (!s) return;
    const handleErrandAssigned = (data) => {
      const notif = {
        id: data.errandId,
        errand_number: data.errandNumber,
        title: data.title,
        priority: data.priority,
        due_date: data.dueDate || null,
        creator_first_name: data.creatorName?.split(' ')[0] || '',
        creator_last_name: data.creatorName?.split(' ').slice(1).join(' ') || '',
        _notifTime: new Date().toISOString(),
        _type: 'assigned',
      };
      seenErrandIdsRef.current.add(data.errandId);
      persistSeenErrandIds(seenErrandIdsRef.current);
      setErrandNotifications(prev => {
        const next = [notif, ...prev].slice(0, 30);
        localStorage.setItem('errand_notifications', JSON.stringify(next));
        return next;
      });
      setErrandUnreadCount(c => {
        const next = c + 1;
        localStorage.setItem('errand_unread_count', String(next));
        return next;
      });
      playFor('errand');
      toast.custom((ti) => <ErrandToast t={ti} errand={data} onClick={() => navigate('/field-tasks')} />, { duration: 5000 });
    };
    const handleErrandCompleted = (data) => {
      const notif = {
        id: `completed-${data.errandId}-${Date.now()}`,
        errand_number: data.errandNumber,
        title: data.title,
        priority: data.priority,
        _completedBy: data.completedBy,
        _notifTime: new Date().toISOString(),
        _type: 'completed',
      };
      setErrandNotifications(prev => {
        const next = [notif, ...prev].slice(0, 30);
        localStorage.setItem('errand_notifications', JSON.stringify(next));
        return next;
      });
      setErrandUnreadCount(c => {
        const next = c + 1;
        localStorage.setItem('errand_unread_count', String(next));
        return next;
      });
      playFor('errand');
      toast.custom((ti) => <ErrandToast t={ti} errand={data} onClick={() => navigate('/field-tasks')} />, { duration: 5000 });
    };
    const handleErrandCheckin = (data) => {
      playFor('errand');
      toast.custom((ti) => (
        <div
          className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
          style={{ borderLeftColor: '#22c55e', cursor: 'pointer', position: 'relative' }}
          onClick={() => { navigate('/field-tasks'); toast.dismiss(ti.id); }}
        >
          <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
          <div className="presence-toast-body">
            <span className="presence-toast-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
              📍 {data.assigneeName} {t('errands.arrivedAt')} #{data.errandNumber}
            </span>
            <span className="presence-toast-status">{data.title}</span>
          </div>
        </div>
      ), { duration: 6000 });
      // Dispatch custom event so ErrandsPage can refetch
      window.dispatchEvent(new CustomEvent('errand:checkin', { detail: data }));
    };
    const handleErrandCheckout = (data) => {
      const mins = data.workedMinutes || 0;
      const dur = Math.floor(mins / 60) > 0 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      playFor('errand');
      toast.custom((ti) => (
        <div
          className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
          style={{ borderLeftColor: '#22c55e', cursor: 'pointer', position: 'relative' }}
          onClick={() => { navigate('/field-tasks'); toast.dismiss(ti.id); }}
        >
          <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
          <div className="presence-toast-body">
            <span className="presence-toast-name" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              🏁 {data.assigneeName} {t('errands.finishedAt')} #{data.errandNumber}
            </span>
            <span className="presence-toast-status">{data.title} — ⏱️ {dur}</span>
          </div>
        </div>
      ), { duration: 6000 });
      window.dispatchEvent(new CustomEvent('errand:checkout', { detail: data }));
    };
    s.on('errand:assigned', handleErrandAssigned);
    s.on('errand:completed', handleErrandCompleted);
    s.on('errand:checkin', handleErrandCheckin);
    s.on('errand:checkout', handleErrandCheckout);
    // Avance parada por parada: sin toast (sería ruido), sólo reenvío a la pantalla de
    // Tareas de Campo. Antes no se reenviaban y la pantalla había que recargarla para
    // ver el progreso (Thiago, 1-oct).
    const silentErrandEvents = ['errand:stop_completed', 'errand:stop_advanced', 'errand:expense_recorded', 'errand:accepted', 'errand:declined', 'errand:cancelled'];
    const silentForwarders = silentErrandEvents.map(ev => [ev, (data) => window.dispatchEvent(new CustomEvent(ev, { detail: data }))]);
    silentForwarders.forEach(([ev, fn]) => s.on(ev, fn));
    return () => {
      s.off('errand:assigned', handleErrandAssigned);
      s.off('errand:completed', handleErrandCompleted);
      s.off('errand:checkin', handleErrandCheckin);
      s.off('errand:checkout', handleErrandCheckout);
      silentForwarders.forEach(([ev, fn]) => s.off(ev, fn));
    };
  }, [navigate, persistSeenErrandIds, chatSocketRef, t]);

  // Listen for HR payment submission socket events (admin toast)
  useEffect(() => {
    const s = chatSocketRef?.current;
    if (!s || !isAdmin) return;
    const handlePaymentSubmitted = (data) => {
      playFor('billing');
      tryOsNotify({
        title: `💰 Pago recibido — ${data.agentName}`,
        body: `${data.amount} — Requiere revisión`,
        link: '/hr/payments',
        tag: `hr-payment-${data.agentName || 'unknown'}-${Date.now()}`,
        requireInteraction: true, // sticky — admin needs to act
      });
      toast.custom((ti) => (
        <div className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`} style={{ borderLeftColor: '#f59e0b', position: 'relative' }}>
          <button style={toastDismissStyle} onClick={() => toast.dismiss(ti.id)}><X size={14} /></button>
          <div className="presence-toast-body">
            <span className="presence-toast-name">💰 {data.agentName}</span>
            <span className="presence-toast-status">Pago recibido: {data.amount} — Requiere revisión</span>
          </div>
        </div>
      ), { duration: 8000 });
    };
    s.on('hr:payment-submitted', handlePaymentSubmitted);
    return () => s.off('hr:payment-submitted', handlePaymentSubmitted);
  }, [chatSocketRef, isAdmin]);

  // Listen for internal note socket events (real-time bell + toast)
  useEffect(() => {
    const s = chatSocketRef?.current;
    if (!s) return;
    const handleInternalNote = (data) => {
      const noteNotif = {
        id: `note-${data.ticketId}-${Date.now()}`,
        ticket_id: data.ticketId,
        ticket_number: data.ticketNumber,
        subject: data.subject,
        company_name: data.companyName,
        label_color: data.label_color,
        priority: data.priority,
        _noteAuthor: data.authorName,
        _notePreview: data.preview,
        _isInternalNote: true,
        _notifTime: new Date().toISOString(),
      };

      setTicketNotifications(prev => {
        const next = [noteNotif, ...prev].slice(0, 30);
        localStorage.setItem('ticket_notifications', JSON.stringify(next));
        return next;
      });

      setTicketUnreadCount(c => {
        const next = c + 1;
        localStorage.setItem('ticket_unread_count', String(next));
        return next;
      });

      playFor('ticket');
      tryOsNotify({
        title: `📝 #${data.ticketNumber}${data.companyName ? ` · ${data.companyName}` : ''}`,
        body: `${data.authorName}: ${data.preview || 'Nota interna'}`,
        link: `/tickets/${data.ticketId}`,
        tag: `note-${data.ticketId}`,
      });

      toast.custom((ti) => (
        <div
          className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
          style={{ borderLeftColor: '#6b7280', cursor: 'pointer', position: 'relative' }}
          onClick={() => { navigate(`/tickets/${data.ticketId}`); toast.dismiss(ti.id); }}
        >
          <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
          <div className="presence-toast-body">
            <span className="presence-toast-name">
              📝 #{data.ticketNumber}
              {data.companyName && (
                <span style={{
                  marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '1px 6px', borderRadius: 4, fontSize: 11,
                  background: data.label_color ? `${data.label_color}22` : 'var(--c-surface-2)',
                  border: `1px solid ${data.label_color || 'var(--c-border)'}`,
                  color: data.label_color || 'var(--c-text-muted)',
                }}>
                  {data.companyName}
                </span>
              )}
            </span>
            <span className="presence-toast-status">{data.authorName}: {data.preview}</span>
          </div>
        </div>
      ), { duration: 5000 });
    };

    s.on('ticket:internal_note', handleInternalNote);
    return () => s.off('ticket:internal_note', handleInternalNote);
  }, [chatSocketRef?.current, navigate]); // eslint-disable-line

  // Invoice notification state — admin only
  const [invoiceNotifications, setInvoiceNotifications] = useState([]);
  const [invoiceUnreadCount, setInvoiceUnreadCount] = useState(0);
  const [invoiceShine, setInvoiceShine] = useState(false);
  const invoiceSeenRef = useRef(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('invoice_notif_seen_ids'));
      return new Set(Array.isArray(stored) ? stored : []);
    } catch { return new Set(); }
  });
  if (typeof invoiceSeenRef.current === 'function') {
    invoiceSeenRef.current = invoiceSeenRef.current();
  }
  const isFirstInvoicePoll = useRef(true);

  useEffect(() => {
    if (!isAdmin && !isDemo) return;
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/invoices/notifications');
        const list = data || [];
        const totalUnreadNow = list.reduce((sum, inv) => sum + inv.unread_count, 0);

        // Per-invoice seen-set keyed by id + last activity timestamp, same pattern
        // as the ticket / payment polls. The timestamp means a genuinely-new reply
        // on an already-seen invoice (newer last_message_at) counts as new, while
        // the SAME unread sitting there across window re-focuses never re-chimes —
        // which is the false "phantom chime on focus" this replaces. Add-only +
        // capped so a transient list hiccup can't resurrect an old key.
        const seen = invoiceSeenRef.current;
        const keyOf = (inv) => `${inv.id}:${inv.last_message_at || ''}`;
        const brandNew = list.filter(inv => !seen.has(keyOf(inv)));
        for (const inv of brandNew) seen.add(keyOf(inv));
        if (seen.size > 200) invoiceSeenRef.current = new Set([...seen].slice(-200));
        try { localStorage.setItem('invoice_notif_seen_ids', JSON.stringify([...invoiceSeenRef.current])); } catch {}

        // First poll seeds silently — update the badge but never chime for unread
        // that was already sitting there before this tab loaded.
        if (isFirstInvoicePoll.current) {
          isFirstInvoicePoll.current = false;
          setInvoiceNotifications(list);
          setInvoiceUnreadCount(totalUnreadNow);
          return;
        }

        if (brandNew.length > 0) {
          playFor('billing');
          setInvoiceShine(true);
          setTimeout(() => setInvoiceShine(false), 6000);

          for (const inv of brandNew.slice(0, 3)) {
            // Native OS banner — tag by invoice id so multiple replies on same invoice collapse
            tryOsNotify({
              title: `${inv.invoice_number ? `Factura #${inv.invoice_number}` : 'Factura'} — ${inv.company_name}`,
              body: `${inv.unread_count} ${inv.unread_count === 1 ? 'mensaje sin leer' : 'mensajes sin leer'}`,
              link: '/invoicing',
              tag: `invoice-${inv.id}`,
            });
            toast.custom((ti) => (
              <div
                className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
                style={{ borderLeftColor: '#0891b2', cursor: 'pointer', position: 'relative' }}
                onClick={() => { navigate('/invoicing'); toast.dismiss(ti.id); }}
              >
                <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
                <div className="presence-toast-body">
                  <span className="presence-toast-name">
                    <Receipt size={12} style={{ marginRight: 4 }} />
                    {inv.invoice_number ? `#${inv.invoice_number}` : t('notifications.invoices')}
                  </span>
                  <span className="presence-toast-status">
                    {inv.company_name} — {inv.unread_count} {inv.unread_count === 1 ? 'mensaje' : 'mensajes'}
                  </span>
                </div>
              </div>
            ), { duration: 5000 });
          }
        }

        setInvoiceNotifications(list);
        setInvoiceUnreadCount(totalUnreadNow);
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, isDemo, isPageVisible]); // eslint-disable-line

  // Auto-clear invoice notifications when viewing invoicing page — mark as read in DB.
  // Note: deps include invoiceUnreadCount so this ALSO fires when new replies arrive
  // while the user is already sitting on /invoicing. Without it, the count would tick
  // up but never get cleared, making notifications "reappear" on every poll.
  const isOnInvoicingPage = location.pathname.startsWith('/invoicing');
  useEffect(() => {
    if (isOnInvoicingPage && invoiceUnreadCount > 0) {
      setInvoiceUnreadCount(0);
      setInvoiceNotifications([]);
      // Actually mark messages as read in DB so they don't reappear on refresh
      api.patch('/invoices/messages-read-all').catch(() => {});
    }
  }, [isOnInvoicingPage, invoiceUnreadCount]); // eslint-disable-line

  // System / scheduled-send notifications used to live in their own state and have their
  // own 30s poll of /notifications/system — but `inAppNotifs` (below) already polls the
  // same endpoint. The duplicate poll caused scheduled-send rows to render twice (once in
  // "Actividad", once in "Programados") and the two states drifted from each other. We
  // now derive both sections from `inAppNotifs` — see SCHEDULED_TYPES + activityNotifs +
  // systemUnread further down.

  // Payment notification state — all internal (non-scoped) users
  const [paymentNotifications, setPaymentNotifications] = useState([]);
  const [paymentUnreadCount, setPaymentUnreadCount] = useState(0);
  const [paymentShine, setPaymentShine] = useState(false);
  const paymentSeenRef = useRef(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('payment_notif_seen_ids'));
      return new Set(Array.isArray(stored) ? stored : []);
    } catch { return new Set(); }
  });
  if (typeof paymentSeenRef.current === 'function') {
    paymentSeenRef.current = paymentSeenRef.current();
  }
  const isFirstPaymentPoll = useRef(true);

  useEffect(() => {
    if (isScoped) return;
    if (user?.role === 'client') return;
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/hr/payment-notifications');
        const payments = data.payments || [];
        const newCount = payments.filter(p => !paymentSeenRef.current.has(p.id)).length;

        if (isFirstPaymentPoll.current) {
          isFirstPaymentPoll.current = false;
          setPaymentNotifications(payments);
          setPaymentUnreadCount(newCount);
          // Seed seen IDs silently
          payments.forEach(p => paymentSeenRef.current.add(p.id));
          localStorage.setItem('payment_notif_seen_ids', JSON.stringify([...paymentSeenRef.current]));
          return;
        }

        // Detect truly new payments (not in seen set)
        const brandNew = payments.filter(p => !paymentSeenRef.current.has(p.id));
        if (brandNew.length > 0) {
          playFor('billing');
          setPaymentShine(true);
          setTimeout(() => setPaymentShine(false), 6000);

          for (const pmt of brandNew.slice(0, 3)) {
            const fmtAmt = parseFloat(pmt.amount_ars || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            const methodLabels = { bank_transfer: 'Transferencia', cash: 'Efectivo', check: 'Cheque' };
            tryOsNotify({
              title: `Pago recibido: $${fmtAmt}`,
              body: `${methodLabels[pmt.payment_method] || pmt.payment_method} — ${pmt.from_first || ''} ${pmt.from_last || ''}`.trim(),
              link: '/my-payroll',
              tag: `payment-${pmt.id}`,
            });
            toast.custom((ti) => (
              <div
                className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
                style={{ borderLeftColor: '#22c55e', cursor: 'pointer', position: 'relative' }}
                onClick={() => { navigate('/my-payroll'); toast.dismiss(ti.id); }}
              >
                <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
                <div className="presence-toast-body">
                  <span className="presence-toast-name">
                    <Wallet size={12} style={{ marginRight: 4 }} />
                    Pago: ${fmtAmt}
                  </span>
                  <span className="presence-toast-status">
                    {methodLabels[pmt.payment_method] || pmt.payment_method} — {pmt.from_first} {pmt.from_last}
                  </span>
                </div>
              </div>
            ), { duration: 5000 });
          }

          // Add to seen
          brandNew.forEach(p => paymentSeenRef.current.add(p.id));
          localStorage.setItem('payment_notif_seen_ids', JSON.stringify([...paymentSeenRef.current]));
        }

        setPaymentNotifications(payments);
        setPaymentUnreadCount(payments.length);
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [isScoped, isPageVisible]); // eslint-disable-line

  // Auto-clear payment notifications when on /my-payroll
  const isOnPayrollPage = location.pathname.startsWith('/my-payroll');
  useEffect(() => {
    if (isOnPayrollPage && paymentUnreadCount > 0) {
      setPaymentUnreadCount(0);
    }
  }, [isOnPayrollPage]); // eslint-disable-line

  // Confirm payment from notification dropdown
  const confirmPaymentFromBell = async (paymentId) => {
    try {
      await api.post(`/hr/payments/${paymentId}/confirm`);
      setPaymentNotifications(prev => prev.filter(p => p.id !== paymentId));
      setPaymentUnreadCount(prev => Math.max(0, prev - 1));
      toast.success(t('common.paymentConfirmed'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  // ── In-app notifications via Socket.io + 30s polling ──
  // Single source of truth for everything in /api/notifications/system. Both the
  // "Actividad" section (ticket lifecycle / SLA) and the "Programados" section
  // (scheduled email/WA sends) are derived from this one array via SCHEDULED_TYPES.
  // Previously we had two independent polls hitting the same endpoint, which double-rendered
  // scheduled-send rows in the bell.
  const SCHEDULED_TYPES = useMemo(() => new Set(['scheduled_email_sent', 'scheduled_wa_sent']), []);
  const [inAppNotifs, setInAppNotifs] = useState([]);
  const inAppUnread = inAppNotifs.filter(n => !n.read).length; // derived; no drift
  const notifSocketRef = useRef(null);

  // Socket.io connection to /notifications namespace — real-time push
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');
    const s = socketIO(`${API_BASE}/notifications`, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnection: true,
      reconnectionDelay: 2000,
    });
    notifSocketRef.current = s;
    s.on('ticket:list-refresh', () => {
      window.dispatchEvent(new Event('ticket-list-refresh'));
    });
    s.on('notification', (notif) => {
      // Dedup: socket can re-fire on reconnect, and the 30s poll may also surface the same row.
      // If id is already in state, skip (no toast, no count drift).
      let isNew = true;
      setInAppNotifs(prev => {
        if (prev.some(n => n.id === notif.id)) { isNew = false; return prev; }
        return [notif, ...prev].slice(0, 50);
      });
      if (!isNew) return;
      playFor('system');
      // OS-level desktop notification — fires only if user enabled it AND the
      // tab is not currently focused (default behavior, configurable in bell header).
      // tag = ticket_id (or notif id) so 5 events on the same ticket replace each other
      // instead of stacking 5 banners.
      tryOsNotify({
        title: notif.title,
        body: notif.body || '',
        link: notif.data?.link,
        tag: notif.data?.ticket_id || notif.data?.invoice_id || notif.id,
        requireInteraction: notif.type === 'sla_breach', // SLA breach is sticky
      });
      // Toast — different style for scheduled-send confirmations vs ticket lifecycle
      if (SCHEDULED_TYPES.has(notif.type)) {
        toast.custom((ti) => (
          <div
            className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
            style={{ borderLeftColor: '#22c55e', cursor: 'pointer', position: 'relative' }}
            onClick={() => { navigate(notif.data?.link || '/invoicing'); toast.dismiss(ti.id); }}
          >
            <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
            <div className="presence-toast-body">
              <span className="presence-toast-name"><CheckCircle2 size={12} style={{ marginRight: 4, color: '#22c55e' }} />{notif.title}</span>
              <span className="presence-toast-status">{notif.body}</span>
            </div>
          </div>
        ), { duration: 5000 });
      } else {
        const emoji = { ticket_new: '🎫', ticket_assigned: '📌', ticket_reply: '💬', sla_warning: '⚠️', sla_breach: '🚨', ticket_stale: '🕐', internal_note: '📝', ticket_status_changed: '📋', client_waiting: '⏳' }[notif.type] || '🔔';
        toast.custom((ti) => (
          <div
            className={`presence-toast ${ti.visible ? 'presence-toast-enter' : 'presence-toast-exit'}`}
            style={{ borderLeftColor: notif.type === 'sla_breach' ? '#ef4444' : (notif.type === 'sla_warning' || notif.type === 'client_waiting') ? '#f59e0b' : '#3b82f6', cursor: 'pointer', position: 'relative' }}
            onClick={() => { if (notif.data?.link) navigate(notif.data.link); toast.dismiss(ti.id); }}
          >
            <button style={toastDismissStyle} onClick={(e) => { e.stopPropagation(); toast.dismiss(ti.id); }}><X size={14} /></button>
            <div className="presence-toast-body">
              <span className="presence-toast-name">{emoji} {notif.title}</span>
              <span className="presence-toast-status" style={{ fontSize: 11 }}>{notif.body?.slice(0, 60)}</span>
            </div>
          </div>
        ), { duration: 5000 });
      }
    });
    return () => { s.disconnect(); notifSocketRef.current = null; };
  }, [user?.id]); // eslint-disable-line

  // Single 30s poll — DB is authoritative for `read`, but we MERGE rather than replace
  // so that very-recent socket pushes (not yet visible to a SELECT-with-7d-window quirk)
  // and dismissed-but-not-yet-confirmed rows survive.
  useEffect(() => {
    if (!user) return;
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/notifications/system');
        const list = data || [];
        const dbIds = new Set(list.map(n => n.id));
        setInAppNotifs(prev => {
          // Keep socket pushes that aren't yet in the DB result (rare, very recent)
          const recentSocketOnly = prev.filter(n => !dbIds.has(n.id) && n.created_at && (Date.now() - new Date(n.created_at).getTime() < 60000));
          const merged = [...list, ...recentSocketOnly];
          return merged.slice(0, 50);
        });
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [user?.id, isPageVisible]); // eslint-disable-line

  const markInAppRead = async () => {
    // Optimistic update first
    setInAppNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await api.patch('/notifications/system/read-all');
    } catch {
      // Server didn't accept it — re-sync from DB so the bell reflects truth on next render
      try {
        const { data } = await api.get('/notifications/system');
        setInAppNotifs(data || []);
      } catch {}
    }
  };

  // Hard-dismiss: removes the row from system_notifications so polling can't bring it back.
  const dismissInAppNotif = async (id) => {
    setInAppNotifs(prev => prev.filter(n => n.id !== id));
    try {
      await api.delete(`/notifications/system/${id}`);
    } catch {
      // If the delete fails, the next poll will restore it — safe.
    }
  };

  // Mark a single notif as read; if the PATCH fails, undo the local state so we don't
  // silently lie to the user about it being read.
  const markOneInAppRead = async (id) => {
    setInAppNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try {
      await api.patch(`/notifications/system/${id}/read`);
    } catch {
      try {
        const { data } = await api.get('/notifications/system');
        setInAppNotifs(data || []);
      } catch {}
    }
  };

  // Derived sections for the bell rendering (single source: inAppNotifs)
  // El panel se cierra desde ocho lugares distintos (la campana, el fondo, y cada
  // ítem al navegar). Un efecto sobre `showNotifications` cubre todos sin repetir.
  useEffect(() => { if (!showNotifications) setNotifFilter('all'); }, [showNotifications]);

  const activityNotifs = inAppNotifs.filter(n => !SCHEDULED_TYPES.has(n.type));

  const systemUnread = inAppNotifs.filter(n => SCHEDULED_TYPES.has(n.type) && !n.read);
  const markSystemRead = markInAppRead; // Programados "Marcar leídos" — same endpoint marks all

  /* ── Filtro de la campana ──────────────────────────────────────────────────
     Pedido de Thiago (3-sep) después de entrevistar a Ariel y a Santi: "un filtro
     en la campana para poder ver qué ticket se atendieron, cuál falta". La campana
     junta OCHO fuentes distintas en una sola lista —actividad, tickets, tareas de
     campo, facturas, programados, pagos, chat y agentes— y cuando hay movimiento
     encontrar una cosa puntual es scrollear.

     Las pastillas salen de los datos reales, no de una lista fija: sólo aparece la
     fuente que hoy tiene algo, con su número. Una fuente vacía no ensucia. Y si hay
     una sola fuente con contenido no se muestra ninguna pastilla, porque un filtro
     de una opción no filtra nada. */
  const notifSections = [
    { key: 'activity', label: t('notifications.activity'), n: activityNotifs.length, show: true },
    { key: 'tickets',  label: t('notifications.tickets'),  n: ticketNotifications.length, show: true },
    { key: 'errands',  label: t('errands.title'),          n: errandNotifications.length, show: true },
    { key: 'invoices', label: t('notifications.invoices'), n: invoiceNotifications.length, show: isAdmin || isDemo },
    { key: 'scheduled',label: t('notifications.scheduled'),n: systemUnread.length,        show: isAdmin || isDemo },
    { key: 'payments', label: t('notifications.payments'), n: paymentNotifications.length, show: isAdmin || isDemo },
    { key: 'chat',     label: t('notifications.chat'),     n: chatNotifications.length,   show: true },
    { key: 'agents',   label: t('notifications.agents'),   n: notifications.length,       show: isManager },
  ].filter(sec => sec.show && sec.n > 0);
  const showNotifFilter = notifSections.length > 1;
  // Si la fuente elegida se vacía (se leyó todo, entró otra cosa), la vista volvería
  // a quedar vacía sin explicación. Se cae sola a "Todo" en vez de dejar al usuario
  // mirando una lista vacía sin saber que hay un filtro puesto.
  const notifFilterActive = notifSections.some(sec => sec.key === notifFilter) ? notifFilter : 'all';
  const showSection = (key) => notifFilterActive === 'all' || notifFilterActive === key;

  // Combined mark-all-read
  const markAllReadCombined = () => {
    markAllRead();
    setTicketUnreadCount(0);
    localStorage.setItem('ticket_unread_count', '0');
    clearChatNotifications();
    setInvoiceUnreadCount(0);
    setInvoiceNotifications([]);
    if (isAdmin) api.patch('/invoices/messages-read-all').catch(() => {});
    setPaymentUnreadCount(0);
    setErrandUnreadCount(0);
    localStorage.setItem('errand_unread_count', '0');
    markInAppRead();
  };

  const totalUnread = (isManager ? unreadCount : 0) + ticketUnreadCount + chatNotifications.length + invoiceUnreadCount + paymentUnreadCount + errandUnreadCount + inAppUnread;

  // Activity-aware heartbeat with idle detection
  useEffect(() => {
    const ACTIVE_THRESHOLD = 3 * 60 * 1000; // 3 min — counts as "actively working"
    const HEARTBEAT_INTERVAL = 30000;        // 30s — always sends, only the idle flag differs
    let lastActivity = Date.now();

    const markActive = () => { lastActivity = Date.now(); };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, markActive, { passive: true }));

    const ping = (idle = false) => api.post('/presence/heartbeat', { idle }).catch(() => {});

    const heartbeat = () => {
      const isActive = (Date.now() - lastActivity) < ACTIVE_THRESHOLD;

      if (!document.hidden && isActive) {
        // User is actively working
        ping(false);
      } else {
        // Idle (tab visible but inactive) or tab hidden — keep pinging as idle
        // so backend keeps them as "away" instead of dropping to "offline"
        ping(true);
      }
    };

    // On tab visibility change, send immediate ping
    const handleVisibility = () => {
      if (document.hidden) {
        ping(true);
      } else {
        markActive();
        ping(false);
      }
    };

    // On page close, signal offline via beacon
    const handleUnload = () => {
      navigator.sendBeacon?.(`${api.defaults.baseURL}/presence/heartbeat-beacon`, new Blob([JSON.stringify({ idle: true, token: localStorage.getItem('accessToken') })], { type: 'application/json' }));
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', handleUnload);

    // Initial heartbeat on mount
    ping(false);
    const interval = setInterval(heartbeat, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(interval);
      events.forEach(e => window.removeEventListener(e, markActive));
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  // Presence watcher — toast when agents come online/go offline
  // Uses stabilization: status must persist for 2+ polls before notifying (eliminates flapping)
  const presenceRef = useRef(null);       // { [id]: { presence, ... } }
  const pendingChanges = useRef({});       // { [id]: { status, since: timestamp } }
  const STABILIZE_MS = 90000;             // 90s — status must be stable this long before notifying
  const STABILIZE_ONLINE_MS = 30000;      // 30s — faster notification for coming online

  useEffect(() => {
    if (!isManager) return;
    if (!isPageVisible) return;
    const poll = async () => {
      try {
        const { data } = await api.get('/presence/agents');
        const prev = presenceRef.current;
        const next = {};
        for (const agent of data) {
          next[agent.id] = agent;
        }
        if (prev) {
          const now = Date.now();
          for (const id of Object.keys(next)) {
            if (id === user?.id) continue;
            const oldStatus = prev[id]?.presence;
            const newStatus = next[id].presence;

            if (oldStatus === newStatus) {
              // Status is stable — check if there's a pending change that's now confirmed
              const pending = pendingChanges.current[id];
              if (pending && pending.status === newStatus) {
                const delay = newStatus === 'online' ? STABILIZE_ONLINE_MS : STABILIZE_MS;
                if (now - pending.since >= delay) {
                  // Status has been stable long enough — notify
                  const agent = next[id];
                  if (newStatus === 'online' && pending.from !== 'online') {
                    toast.custom((ti) => <PresenceToast t={ti} agent={agent} isOnline={true} />, { duration: 5000 });
                    addNotification(agent, true);
                  } else if (newStatus !== 'online' && pending.from === 'online') {
                    toast.custom((ti) => <PresenceToast t={ti} agent={agent} isOnline={false} />, { duration: 5000 });
                    addNotification(agent, false);
                  }
                  delete pendingChanges.current[id];
                }
              }
              continue;
            }

            // Status changed — start or update pending timer
            const existing = pendingChanges.current[id];
            if (!existing || existing.status !== newStatus) {
              pendingChanges.current[id] = { status: newStatus, from: oldStatus, since: now };
            }
          }
          // Clean up pending changes for agents no longer in the list
          for (const id of Object.keys(pendingChanges.current)) {
            if (!next[id]) delete pendingChanges.current[id];
          }
        }
        presenceRef.current = next;
      } catch {}
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [isManager, user?.id, addNotification, isPageVisible]);

  // ── Passive click heatmap (raw layer below ui_actions) ──
  // Dead clicks — where agents tap and nothing happens — are the backlog the
  // UI writes by itself. Idempotent: PortalLayout calls the same installer.
  useEffect(() => { installHeatmap(); }, []);

  // ── Module visit tracking ──
  const lastTrackedModule = useRef(null);
  useEffect(() => {
    const moduleKey = moduleFromPath(location.pathname);
    // Must run BEFORE the early return below: navigating to an unmapped route
    // yields a null moduleKey, and if that returned first the previous module's
    // clock would keep running forever. startDwell is a no-op when the module
    // is unchanged, so calling it on every navigation is safe.
    startDwell(moduleKey);
    if (!moduleKey || moduleKey === lastTrackedModule.current) return;
    lastTrackedModule.current = moduleKey;
    api.post('/module-visits', { moduleKey }).catch(() => {});
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : 'U';

  const mainNavItems = [
    { key: 'dashboard', to: '/', label: t('nav.dashboard'), icon: <LayoutDashboard size={16} />, end: true },
    // Cockpit ("Para mí hoy") — owner-only, same gate as the private inbox
    ...(user?.canSeePrivateTickets ? [{ key: 'cockpit', to: '/cockpit', label: t('nav.cockpit'), icon: <Compass size={16} /> }] : []),
    { key: 'companies', to: '/companies', label: t('nav.clients'), icon: <Building2 size={16} /> },
    ...((isAdmin || isDemo) ? [{ key: 'client_access', to: '/client-access', label: t('nav.clientAccess'), icon: <UserCheck size={16} /> }] : []),
    ...(isAdmin ? [{ key: 'ticket_import', to: '/ticket-import', label: t('nav.ticketImport'), icon: <Upload size={16} /> }] : []),
    ...(isAdmin ? [{ key: 'storage', to: '/storage', label: t('nav.storage'), icon: <HardDrive size={16} /> }] : []),
    {
      key: 'group_network',
      label: t('nav.groupNetwork'),
      render: () => (
        <NavGroup icon={Network} label={t('nav.groupNetwork')} pathPrefixes={['/monitor', '/sonos', '/topology', '/vpn', '/resource-monitor']} reorderMode={reorderMode}>
          <NavLink to="/monitor" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Radio size={14} /> {t('monitor.title')}
            {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="monitor" />}
          </NavLink>
          <NavLink to="/sonos" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Speaker size={14} /> {t('sonos.title')}
            {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="monitor" />}
          </NavLink>
          {hasModule('topology') && (
            <NavLink to="/topology" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Network size={14} /> {t('topology.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="topology" />}
            </NavLink>
          )}
          {hasModule('vpn') && (
            <NavLink to="/vpn" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Shield size={14} /> {t('vpn.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="vpn" />}
            </NavLink>
          )}
          {hasModule('resource_monitor') && (
            <NavLink to="/resource-monitor" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Activity size={14} /> {t('resourceMonitor.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="resource_monitor" />}
            </NavLink>
          )}
        </NavGroup>
      ),
    },
    ...(hasModule('remote_access') ? [{
      key: 'group_remote',
      label: t('nav.groupRemote'),
      render: () => (
        <NavGroup icon={Monitor} label={t('nav.groupRemote')} pathPrefixes={['/remote-access', '/remote-assist']} reorderMode={reorderMode}>
          <NavLink to="/remote-access" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Monitor size={14} /> {t('nav.remoteAccess')}
            {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="remote_access" />}
          </NavLink>
          <NavLink to="/remote-assist" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <ScreenShare size={14} /> {t('nav.remoteAssist')}
          </NavLink>
        </NavGroup>
      ),
    }] : []),
    ...(hasModule('quick_launch') ? [{ key: 'quick_launch', to: '/quick-launch', label: t('nav.quickLaunch'), icon: <Rocket size={16} /> }] : []),
    ...(hasModule('live_assist_pro') ? [{ key: 'live_assist_pro', to: '/live-assist-pro', label: t('nav.liveAssistPro'), icon: <ScreenShare size={16} /> }] : []),
    // voice_calls moved into the Communications group (it's a comms channel)
    ...(hasModule('assets') ? [{ key: 'assets', to: '/assets', label: t('nav.assets'), icon: <HardDrive size={16} /> }] : []),
    ...(hasModule('workshop') ? [{ key: 'workshop', to: '/workshop', label: t('nav.workshop'), icon: <Wrench size={16} /> }] : []),
    ...(hasModule('prepaid_lines') ? [{ key: 'prepaid_lines', to: '/prepaid-lines', label: t('nav.prepaidLines'), icon: <Smartphone size={16} /> }] : []),
    ...(hasModule('racks') ? [{ key: 'racks', to: '/racks', label: t('nav.racks'), icon: <Server size={16} /> }] : []),
    ...(hasModule('google_business') ? [{ key: 'google_business', to: '/google-business', label: t('nav.googleBusiness'), icon: <Store size={16} /> }] : []),
    ...(hasModule('legacy_sheets') ? [{ key: 'legacy_sheets', to: '/legacy-sheets', label: t('nav.legacySheets'), icon: <FileSpreadsheet size={16} /> }] : []),
    ...(hasModule('mercadolibre') ? [{ key: 'mercadolibre', to: '/mercadolibre', label: t('nav.mercadoLibre'), icon: <ShoppingCart size={16} /> }] : []),
    ...(hasModule('monitoring') ? [{ key: 'monitoring', to: '/monitoring', label: t('monitoring.title'), icon: <Eye size={16} /> }] : []),
    ...(hasModule('web_review') ? [{ key: 'web_review', to: '/web-review', label: t('nav.webReview'), icon: <Globe size={16} /> }] : []),
    ...(hasModule('cctv') && !hasModule('assets') ? [{ key: 'cctv', to: '/cctv', label: t('nav.cctv'), icon: <Video size={16} /> }] : []),
    ...(isAdmin ? [{ key: 'smart_homes', to: '/smart-homes', label: t('nav.smartHomes'), icon: <Home size={16} /> }] : []),
    ...(isAdmin ? [{ key: 'recruiting', to: '/recruiting', label: t('nav.recruiting'), icon: <UserCheck size={16} /> }] : []),
    // video_wall / site_content / offboarding moved into the Admin Tools group (see group_admin_tools below)
    ...((hasModule('agent_status') || isManager || isDemo) ? [{
      key: 'group_team',
      label: t('nav.groupTeam'),
      render: () => (
        <NavGroup icon={Users} label={t('nav.groupTeam')} pathPrefixes={['/team', '/agents', '/agent-actions']} reorderMode={reorderMode}>
          {(hasModule('agent_status') || isManager || isDemo) && (
            <NavLink to="/team" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <BarChart3 size={14} /> {t('team.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="team_panel" />}
            </NavLink>
          )}
          {(isManager || isDemo) && (
            <NavLink to="/agents" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Users size={14} /> {t('nav.agents')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="agents" />}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/agent-actions" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Bell size={14} /> {t('nav.agentActions')}
              {!reorderMode && <ModuleVisitBadge moduleKey="agent_actions" />}
            </NavLink>
          )}
        </NavGroup>
      ),
    }] : []),
    // portal_permissions / game_admin / usage_hotspots / worker_health / system_activity moved into the Admin Tools group
    ...(hasModule('game') ? [{ key: 'game', to: '/game', label: t('nav.game'), icon: <Gamepad2 size={16} /> }] : []),
    // Prode: el Mundial terminó (jul-2026) — queda como archivo consultable SOLO para
    // Andy (mismo patrón que el Video Wall). Ya no depende de hasModule('prode').
    ...(user?.id === 'eafdff80-02d2-4eef-9e5c-19f57761b85d' ? [{ key: 'prode', to: '/prode', label: t('nav.prode'), icon: <Trophy size={16} /> }] : []),
    // HR vive dentro del grupo Finanzas (group_invoicing), con Facturación y las cuentas.
    ...((!isClient || isDemo) ? [{ key: 'my_payroll', to: '/my-payroll', label: t('nav.myPayroll'), icon: <Wallet size={16} /> }] : []),
    ...(hasModule('chat') ? [{ key: 'chat', to: '/chat', label: t('nav.chat'), icon: <MessageSquare size={16} />, badge: chatUnread }] : []),
    ...(hasModule('feedback_requests') ? [{ key: 'feedback_requests', to: '/feedback', label: t('nav.feedbackRequests'), icon: <Lightbulb size={16} /> }] : []),
    ...(hasModule('changelog') ? [{ key: 'changelog', to: '/changelog', label: t('nav.changelog'), icon: <Sparkles size={16} />, badge: changelogUnseen }] : []),
    ...((hasModule('knowledge_base') || hasModule('rag_search') || hasModule('procedures') || hasModule('provisioning')) ? [{
      key: 'group_knowledge',
      label: t('nav.groupKnowledge'),
      render: () => (
        <NavGroup icon={BookOpen} label={t('nav.groupKnowledge')} pathPrefixes={['/kb', '/rag-search', '/procedures', '/provisioning']} reorderMode={reorderMode}>
          {hasModule('knowledge_base') && (
            <NavLink to="/kb" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <BookOpen size={14} /> {t('nav.knowledgeBase')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="knowledge_base" />}
            </NavLink>
          )}
          {hasModule('rag_search') && (
            <NavLink to="/rag-search" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Search size={14} /> {t('nav.ragSearch')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="rag_search" />}
            </NavLink>
          )}
          {hasModule('procedures') && (
            <NavLink to="/procedures" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <ClipboardList size={14} /> {t('nav.procedures')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="procedures" />}
            </NavLink>
          )}
          {hasModule('provisioning') && (
            <NavLink to="/provisioning" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Wrench size={14} /> {t('nav.provisioning')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="provisioning" />}
            </NavLink>
          )}
        </NavGroup>
      ),
    }] : []),
    // Admin/demo ven Presupuestos dentro de Finanzas; un agente con el módulo, suelto.
    ...(hasModule('budgets') && !(isAdmin || isDemo) ? [{ key: 'budgets', to: '/budgets', label: t('nav.budgets'), icon: <FileText size={16} /> }] : []),
    ...((hasModule('vault') || hasModule('2fa_codes') || hasModule('access_control') || hasModule('file_permissions') || fileServerHasData || hasModule('audit_log')) ? [{
      key: 'group_security',
      label: t('nav.groupSecurity'),
      render: () => (
        <NavGroup icon={ShieldCheck} label={t('nav.groupSecurity')} pathPrefixes={['/vault', '/2fa-codes', '/access-control', '/file-permissions', '/audit-log']} reorderMode={reorderMode}>
          {hasModule('vault') && (
            <NavLink to="/vault" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <KeyRound size={14} /> {t('nav.vault')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="vault" />}
            </NavLink>
          )}
          {hasModule('2fa_codes') && (
            <NavLink to="/2fa-codes" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <ShieldCheck size={14} /> {t('nav.twoFaCodes')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="2fa_codes" />}
            </NavLink>
          )}
          {hasModule('access_control') && (
            <NavLink to="/access-control" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Fingerprint size={14} /> {t('accessControl.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="access_control" />}
            </NavLink>
          )}
          {['eafdff80-02d2-4eef-9e5c-19f57761b85d', 'cd8b13cf-8478-444b-8d8c-5b5daa87a3c8'].includes(user?.id) && (
            <NavLink to="/attendance" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Clock size={14} /> {t('attendance.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="attendance" />}
            </NavLink>
          )}
          {(hasModule('file_permissions') || fileServerHasData) && (
            <NavLink to="/file-permissions" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <FolderLock size={14} /> {t('filePermissions.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="file_permissions" />}
            </NavLink>
          )}
          {hasModule('audit_log') && (
            <NavLink to="/audit-log" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <History size={14} /> {t('nav.auditLog')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="audit_log" />}
            </NavLink>
          )}
        </NavGroup>
      ),
    }] : []),
    ...((((isAdmin || isDemo) && hasModule('whatsapp')) || hasModule('email_accounts') || hasModule('comms_monitor') || hasModule('voice_calls') || isAdmin) ? [{
      key: 'group_comms',
      label: t('nav.groupComms'),
      render: () => (
        <NavGroup icon={MessageCircle} label={t('nav.groupComms')} pathPrefixes={['/whatsapp-settings', '/email-accounts', '/comms-monitor', '/inbound-rules', '/telegram-routes', '/announcements', '/llamadas']} reorderMode={reorderMode}>
          {hasModule('voice_calls') && (
            <NavLink to="/llamadas" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Phone size={14} /> {t('nav.voiceCalls')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="voice_calls" />}
            </NavLink>
          )}
          {(isAdmin || isDemo) && hasModule('whatsapp') && (
            <NavLink to="/whatsapp-settings" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <MessageCircle size={14} /> {t('nav.whatsapp')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="whatsapp" />}
            </NavLink>
          )}
          {hasModule('email_accounts') && (
            <NavLink to="/email-accounts" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Mail size={14} /> {t('emailAccounts.title')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="email_accounts" />}
            </NavLink>
          )}
          {hasModule('comms_monitor') && (
            <NavLink to="/comms-monitor" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Mail size={14} /> COMMs Monitor
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="comms_monitor" />}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/inbound-rules" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Filter size={14} /> {t('nav.inboundRules')}
              {!reorderMode && <ModuleVisitBadge moduleKey="inbound_rules" />}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/telegram-routes" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Send size={14} /> {t('nav.telegramRoutes')}
              {!reorderMode && <ModuleVisitBadge moduleKey="telegram_routes" />}
            </NavLink>
          )}
          {isAdmin && (
            <NavLink to="/announcements" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <Megaphone size={14} /> {t('nav.announcements')}
              {!reorderMode && <ModuleVisitBadge moduleKey="announcements" />}
            </NavLink>
          )}
        </NavGroup>
      ),
    }] : []),
    ...(hasModule('errands') ? [{ key: 'errands', to: '/field-tasks', label: t('nav.errands'), icon: <Truck size={16} /> }] : []),
    ...(hasModule('requests') ? [{ key: 'requests', to: '/requests', label: t('nav.requests'), icon: <CalendarCheck size={16} /> }] : []),
    { key: 'report_review', to: '/report-review', label: t('nav.reportReview'), icon: <ClipboardCheck size={16} /> },
    ...(hasModule('reports') ? [{ key: 'reports', to: '/reports', label: t('nav.reports'), icon: <BarChart3 size={16} /> }] : []),
    ...(hasModule('calendar') ? [{ key: 'calendar', to: '/calendar', label: t('nav.calendar'), icon: <CalendarDays size={16} /> }] : []),
    ...((hasModule('backup_monitor') || hasModule('ops_monitor') || hasModule('vps_health') || hasModule('dmarc') || hasModule('ip_intel')) ? [{
      key: 'group_health_checks',
      label: t('nav.healthChecks'),
      render: () => (
        <>
          <div
            className="nav-item"
            style={{ cursor: 'pointer' }}
            onClick={toggleHealth}
          >
            {reorderMode && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
            <ShieldCheck size={16} />
            {t('nav.healthChecks')}
            <ChevronDown size={12} style={{ marginLeft: 'auto', transform: healthOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </div>
          {healthOpen && !reorderMode && (
            <div style={{ marginLeft: 12, borderLeft: '1px solid var(--c-border)', paddingLeft: 8 }}>
              {hasModule('backup_monitor') && (
                <NavLink to="/backup-monitor" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <HardDrive size={14} /> {t('backupMonitor.title')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="backup_monitor" />}
                </NavLink>
              )}
              {hasModule('ops_monitor') && (
                <NavLink to="/ops-monitor" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Activity size={14} /> {t('opsMonitor.title')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="ops_monitor" />}
                </NavLink>
              )}
              {hasModule('vps_health') && (
                <NavLink to="/vps-health" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Server size={14} /> {t('vpsHealth.title')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="vps_health" />}
                </NavLink>
              )}
              {hasModule('dmarc') && (
                <NavLink to="/dmarc" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <ShieldCheck size={14} /> {t('dmarc.title')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="dmarc" />}
                </NavLink>
              )}
              {hasModule('ip_intel') && (
                <NavLink to="/ip-intelligence" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Globe size={14} /> {t('ipIntel.navTitle')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="ip_intel" />}
                </NavLink>
              )}
            </div>
          )}
        </>
      ),
    }] : []),
    { key: 'configurations', to: '/configurations', label: t('configurations.title'), icon: <FileText size={16} /> },
    // Admin ve Costos cloud y Costos de herramientas dentro de Finanzas.
    ...(hasModule('cloud_costs') && !isAdmin ? [{ key: 'cloud_costs', to: '/cloud-costs', label: t('cloudCosts.title'), icon: <Cloud size={16} /> }] : []),
    ...(hasModule('deployments') ? [{ key: 'deployments', to: '/deployments', label: t('deployments.title'), icon: <Rocket size={16} /> }] : []),
    ...(hasModule('projects') ? [{ key: 'projects', to: '/projects', label: t('projects.title'), icon: <ClipboardCheck size={16} /> }] : []),
    ...(hasModule('personnel') ? [{ key: 'personnel', to: '/personnel', label: t('personnel.title'), icon: <UserPlus size={16} /> }] : []),
    ...(hasModule('diagnostics') ? [{ key: 'diagnostics', to: '/diagnostics', label: t('nav.diagnostics'), icon: <Cpu size={16} /> }] : []),
    ...(hasModule('settings') ? [{ key: 'settings', to: '/settings', label: t('nav.settings'), icon: <Settings size={16} /> }] : []),
    // ── Admin Tools: collapses the scattered admin-only meta-tools into one group ──
    ...(isAdmin ? [{
      key: 'group_admin_tools',
      label: t('nav.adminTools'),
      render: () => (
        <NavGroup icon={Cog} label={t('nav.adminTools')} pathPrefixes={['/it-costs', '/portal-permissions', '/game-admin', '/usage-hotspots', '/session-replay', '/worker-health', '/system-activity', '/site-content', '/offboarding', '/website-reports', '/campaigns', '/data-integrity', '/api-tokens', '/portal-studio', '/ai-dashboard', '/growth', '/video-wall', '/wa-avatars']} reorderMode={reorderMode}>
          <NavLink to="/ai-dashboard" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Sparkles size={14} /> {t('nav.aiDashboard')}
          </NavLink>
          <NavLink to="/growth" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <TrendingUp size={14} /> {t('nav.growth')}
          </NavLink>
          <NavLink to="/usage-hotspots" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Flame size={14} /> {t('nav.usageHotspots')}
          </NavLink>

          <NavLink to="/session-replay" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <MousePointer2 size={14} /> {t('nav.sessionReplay')}
          </NavLink>
          <NavLink to="/worker-health" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Activity size={14} /> {t('nav.workerHealth')}
          </NavLink>
          <NavLink to="/system-activity" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Activity size={14} /> {t('nav.systemActivity')}
          </NavLink>
          <NavLink to="/portal-permissions" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Shield size={14} /> {t('nav.portalPermissions')}
          </NavLink>
          <NavLink to="/site-content" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Globe size={14} /> {t('nav.siteContent')}
          </NavLink>
          <NavLink to="/offboarding" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <LogOut size={14} /> {t('nav.offboarding')}
          </NavLink>
          <NavLink to="/website-reports" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <FileBarChart size={14} /> {t('nav.websiteReports')}
            {!reorderMode && <ModuleVisitBadge moduleKey="website_reports" />}
          </NavLink>
          <NavLink to="/it-costs" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Wallet size={14} /> {t('nav.itCosts')}
          </NavLink>
          <NavLink to="/campaigns" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Send size={14} /> {t('nav.campaigns')}
          </NavLink>
          <NavLink to="/data-integrity" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <ShieldCheck size={14} /> {t('nav.dataIntegrity')}
          </NavLink>
          <NavLink to="/api-tokens" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Bot size={14} /> {t('nav.apiTokens')}
          </NavLink>
          <NavLink to="/portal-studio" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <LayoutDashboard size={14} /> {t('nav.portalStudio')}
          </NavLink>
          <NavLink to="/wa-avatars" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <MessageCircle size={14} /> {t('nav.waAvatars')}
          </NavLink>
          <NavLink to="/game-admin" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
            <Sparkles size={14} /> {t('nav.gameAdmin')}
          </NavLink>
          {/* Video Wall — locked to Andy's user id (see routes/videoWall.js) */}
          {user?.id === 'eafdff80-02d2-4eef-9e5c-19f57761b85d' && (
            <NavLink to="/video-wall" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
              <LayoutGrid size={14} /> {t('nav.videoWall')}
            </NavLink>
          )}
        </NavGroup>
      ),
    }] : []),
  ];

  const SidebarContent = () => {
    // Build unified orderable nav items (groups + flat items)
    const allItems = [
      {
        key: 'group_tickets',
        label: t('nav.tickets'),
        render: () => (
          <>
            <NavLink
              to="/tickets"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              {reorderMode && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
              <Ticket size={16} />
              {t('nav.tickets')}
              {attentionCount > 0 && !reorderMode && (
                <span
                  className={`nav-badge${attentionShine ? ' nav-badge-shine' : ''}`}
                  title={t('notifications.ticketsAttentionTooltip', { defaultValue: 'Tickets esperando tu respuesta' })}
                >
                  {attentionCount > 99 ? '99+' : attentionCount}
                </span>
              )}
              {/* Surface the Notificaciones (active monitor incidents) badge on the
                  collapsed parent so it's never hidden; when the group is open it
                  shows on the Notificaciones sub-item instead (no duplicate). */}
              {!ticketsOpen && notificationsCount > 0 && !reorderMode && (
                <span
                  className={`nav-badge nav-badge-amber${notificationsShine ? ' nav-badge-shine' : ''}`}
                  title={t('notifications.activeIncidentsTooltip')}
                >
                  {notificationsCount > 99 ? '99+' : notificationsCount}
                </span>
              )}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="tickets" />}
              <ChevronDown className="nav-chevron" size={16} onClick={toggleTickets} style={{ marginLeft: 'auto', cursor: 'pointer', padding: 4, margin: -4, boxSizing: 'content-box', transform: ticketsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </NavLink>
            {ticketsOpen && !reorderMode && (
              <div style={{ marginLeft: 12, borderLeft: '1px solid var(--c-border)', paddingLeft: 8 }}>
                <NavLink to="/my-tickets" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Inbox size={14} /> {t('nav.myTickets')}
                </NavLink>
                <NavLink to="/tickets/review-queue" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <ListChecks size={14} /> {t('nav.reviewQueue')}
                </NavLink>
                <NavLink to="/trash" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Trash2 size={14} /> {t('nav.trash')}
                </NavLink>
                <NavLink to="/spam" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <AlertOctagon size={14} /> {t('nav.spam')}
                </NavLink>
                <NavLink to="/notifications" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Bell size={14} /> {t('nav.notifications')}
                  {notificationsCount > 0 && (
                    <span
                      className={`nav-badge nav-badge-amber${notificationsShine ? ' nav-badge-shine' : ''}`}
                      title={t('notifications.activeIncidentsTooltip')}
                    >
                      {notificationsCount > 99 ? '99+' : notificationsCount}
                    </span>
                  )}
                </NavLink>
                <NavLink to="/validation" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => { setSidebarOpen(false); markOtpSeen(); }}>
                  <KeyRound size={14} /> {t('nav.validation')}
                  {validationCount > 0 && (
                    <span className={`nav-badge${validationShine ? ' nav-badge-shine' : ''}`}>
                      {validationCount > 99 ? '99+' : validationCount}
                    </span>
                  )}
                </NavLink>
                {/* Vista a medida de UN cliente nuestro, con el nombre escrito a mano. En la
                    instancia de otro cliente no tiene por qué existir — y menos con el nombre
                    de un tercero a la vista. */}
                {marca.esAyudaTecno && (
                  <NavLink to="/papelera" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                    <Building2 size={14} /> Papelera Berazategui
                  </NavLink>
                )}
                {user?.canSeePrivateTickets && (
                  <NavLink to="/tickets-private" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                    <FolderLock size={14} /> {t('nav.privateInbox')}
                  </NavLink>
                )}
              </div>
            )}
          </>
        ),
      },
      // Supplies nav group — hidden when assets module is enabled (supplies becomes a tab inside Assets)
      ...(!hasModule('assets') ? [{
        key: 'group_supplies',
        label: t('nav.supplies'),
        render: () => (
          <>
            <NavLink
              to="/supplies"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              {reorderMode && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
              <Printer size={16} />
              {t('nav.supplies')}
              {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="supplies" />}
              <ChevronDown className="nav-chevron" size={16} onClick={toggleSupplies} style={{ marginLeft: 'auto', cursor: 'pointer', padding: 4, margin: -4, boxSizing: 'content-box', transform: suppliesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </NavLink>
            {suppliesOpen && !reorderMode && (
              <div style={{ marginLeft: 12, borderLeft: '1px solid var(--c-border)', paddingLeft: 8 }}>
                <NavLink to="/supplies/analytics" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <BarChart3 size={14} /> {t('nav.suppliesAnalytics')}
                </NavLink>
                <NavLink to="/supplies/toner" className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                  <Package size={14} /> {t('nav.tonerInventory')}
                </NavLink>
                {SUPPLY_CLIENTS.map((sc) => (
                  <React.Fragment key={sc.handle}>
                    <NavLink to={`/supplies/${sc.handle}`} className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                      <Building2 size={14} /> {sc.name}
                    </NavLink>
                    <NavLink to={`/supplies/${sc.handle}/analytics`} className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                      <BarChart3 size={14} /> {sc.name} · {t('nav.suppliesAnalytics')}
                    </NavLink>
                    <NavLink to={`/supplies/${sc.handle}/toner`} className={({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                      <Package size={14} /> {sc.name} · {t('nav.tonerInventory')}
                    </NavLink>
                  </React.Fragment>
                ))}
              </div>
            )}
          </>
        ),
      }] : []),
      // Finanzas: todo lo que es plata en un solo grupo — Facturación (con Cobranzas
      // adentro), emisores, cuentas corrientes de clientes, cuentas con proveedores,
      // HR, presupuestos y costos. Antes eran siete entradas sueltas en la barra.
      // Mantiene la key 'group_invoicing' a propósito: la usan el orden personalizado
      // de cada usuario, la vista por categorías y el filtro de módulos por instancia.
      ...((isAdmin || isDemo) ? [{
        key: 'group_invoicing',
        label: t('nav.groupFinance'),
        render: () => {
          const inInstance = (k) => !marca.modules || marca.modules.includes(k);
          const sub = ({ isActive }) => `nav-item nav-item-sub ${isActive ? 'active' : ''}`;
          const close = () => setSidebarOpen(false);
          return (
            <NavGroup icon={Landmark} label={t('nav.groupFinance')} reorderMode={reorderMode} badge={invoiceUnreadCount}
              pathPrefixes={['/invoicing', '/billing-entities', '/accounts', '/supplier-accounts', '/hr', '/budgets', '/costs', '/tool-costs', '/cloud-costs']}>
              {inInstance('invoicing') && (
                <NavLink to="/invoicing" className={sub} onClick={close}>
                  <Receipt size={14} /> {t('invoicing.title')}
                  {isAdmin && !reorderMode && <ModuleVisitBadge moduleKey="invoicing" />}
                  {invoiceUnreadCount > 0 && (
                    <span className={`nav-badge${invoiceShine ? ' nav-badge-shine' : ''}`} style={{ marginLeft: 6 }}>
                      {invoiceUnreadCount > 99 ? '99+' : invoiceUnreadCount}
                    </span>
                  )}
                </NavLink>
              )}
              {inInstance('invoicing') && (
                <NavLink to="/billing-entities" className={sub} onClick={close}><Landmark size={14} /> {t('invoicing.billingEntities')}</NavLink>
              )}
              {isAdmin && inInstance('customer_accounts') && (
                <NavLink to="/accounts" className={sub} onClick={close}>
                  <Wallet size={14} /> {t('accounts.title')}
                  {!reorderMode && <ModuleVisitBadge moduleKey="customer_accounts" />}
                </NavLink>
              )}
              {canUseSupplierAccounts(user, isImpersonating) && inInstance('supplier_accounts') && (
                <NavLink to="/supplier-accounts" className={sub} onClick={close}><Scale size={14} /> {t('supplierAccounts.title')}</NavLink>
              )}
              {inInstance('hr') && (
                <NavLink to="/hr" className={sub} onClick={close}><Wallet size={14} /> {t('nav.hr')}</NavLink>
              )}
              {hasModule('budgets') && inInstance('budgets') && (
                <NavLink to="/budgets" className={sub} onClick={close}><FileText size={14} /> {t('nav.budgets')}</NavLink>
              )}
              {/* Costos y Claves — owner-only (mismo lock que routes/costsHub.js): sólo Andy y
                  nunca impersonando. Para él reemplaza a Costos de herramientas, que sigue
                  enlazada desde adentro y andando por URL. */}
              {canUseCostsHub(user, isImpersonating) && (
                <NavLink to="/costs" className={sub} onClick={close}><Wallet size={14} /> {t('costsHub.title')}</NavLink>
              )}
              {isAdmin && !canUseCostsHub(user, isImpersonating) && inInstance('tool_costs') && (
                <NavLink to="/tool-costs" className={sub} onClick={close}><Wallet size={14} /> {t('toolCosts.title')}</NavLink>
              )}
              {isAdmin && hasModule('cloud_costs') && inInstance('cloud_costs') && (
                <NavLink to="/cloud-costs" className={sub} onClick={close}><Cloud size={14} /> {t('cloudCosts.title')}</NavLink>
              )}
            </NavGroup>
          );
        },
      }] : []),
      // Mis plataformas — owner-only: la vitrina para abrir cada plataforma que
      // construimos en una reunión con un cliente. Sólo Andy, nunca impersonando.
      ...(canUseMyPlatforms(user, isImpersonating) ? [{
        key: 'my_platforms',
        label: t('myPlatforms.title'),
        render: () => (
          <NavLink
            to="/my-platforms"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            title={t('myPlatforms.title')}
            onClick={() => setSidebarOpen(false)}
          >
            {reorderMode && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
            <LayoutGrid size={16} />
            {t('myPlatforms.title')}
          </NavLink>
        ),
      }] : []),
      ...mainNavItems,
    ]
      // Un módulo que no forma parte de ESTA instancia no se dibuja. Es lo que hacía que el
      // admin de un colegio viera Facturación, Cuenta Corriente, Líneas Prepagas, Mercado
      // Libre, HR y Laboratorio: los admin saltean el control de módulos por usuario, y el
      // admin de un cliente es admin. Esto no es un permiso —es qué producto se le vendió—
      // así que se decide por instancia y el backend lo hace cumplir igual (ver requireModule).
      .filter(it => !marca.modules || !it?.key || marca.modules.includes(String(it.key).replace(/^group_/, '')) || marca.modules.includes(it.key));

    // ── Resolve list per view mode ──
    // - default: original order from allItems (no reorder applied)
    // - custom: navOrder applied; reorder enabled
    // - alphabetical: sorted by label A→Z (flat)
    // - categories: grouped by NAV_CATEGORY_MAP (flat sections, no drag)
    const effectiveMode = reorderMode ? 'custom' : navViewMode;
    let orderedItems;
    if (effectiveMode === 'custom' && navOrder) {
      orderedItems = [
        ...navOrder.filter(k => allItems.find(i => i.key === k)).map(k => allItems.find(i => i.key === k)),
        ...allItems.filter(i => !navOrder.includes(i.key)),
      ];
    } else if (effectiveMode === 'alphabetical') {
      orderedItems = [...allItems].sort((a, b) =>
        (a.label || a.key).localeCompare(b.label || b.key, undefined, { sensitivity: 'base' })
      );
    } else {
      orderedItems = allItems;
    }
    const orderedKeys = orderedItems.map(i => i.key);

    // Group items by category (used only in 'categories' mode)
    const itemsByCategory = {};
    if (effectiveMode === 'categories') {
      for (const item of allItems) {
        const cat = NAV_CATEGORY_MAP[item.key] || 'other';
        if (!itemsByCategory[cat]) itemsByCategory[cat] = [];
        itemsByCategory[cat].push(item);
      }
      // Sort items within each category alphabetically for consistency
      for (const cat of Object.keys(itemsByCategory)) {
        itemsByCategory[cat].sort((a, b) =>
          (a.label || a.key).localeCompare(b.label || b.key, undefined, { sensitivity: 'base' })
        );
      }
    }

    const renderNavItem = (item, idx, dragEnabled) => {
      const wrapperStyle = {};
      if (dragEnabled && dragOverIdx === idx) {
        wrapperStyle.boxShadow = 'inset 0 2px 0 0 var(--c-brand)';
      }
      const dragProps = dragEnabled ? {
        draggable: true,
        onDragStart: (e) => { dragItemIdx.current = idx; e.dataTransfer.effectAllowed = 'move'; },
        onDragOver: (e) => { e.preventDefault(); setDragOverIdx(idx); },
        onDrop: () => handleNavDrop(orderedKeys, idx),
        onDragEnd: handleNavDragEnd,
        onDragLeave: () => setDragOverIdx(null),
      } : {};

      if (item.render) {
        return (
          <div key={item.key} {...dragProps} style={{ ...wrapperStyle, cursor: dragEnabled ? 'grab' : undefined }}>
            {item.render()}
          </div>
        );
      }

      return (
        <div key={item.key} {...dragProps} style={{ ...wrapperStyle, position: 'relative' }}>
          <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            // Plegada no se lee el nombre: el title lo devuelve al pasar el mouse.
            title={typeof item.label === 'string' ? item.label : undefined}
            onClick={() => setSidebarOpen(false)}
          >
            {dragEnabled && <GripVertical size={12} style={{ color: 'var(--c-text-muted)', flexShrink: 0, marginRight: -4 }} />}
            {item.icon}
            {/* El label va en un span propio: plegada, el CSS lo esconde y deja el
                ícono. Como nodo de texto suelto no había forma de ocultarlo sin
                tocar cada pantalla. */}
            <span className="nav-item-label">{item.label}</span>
            {item.badge > 0 && (
              <span className="nav-badge" style={{ marginLeft: 'auto' }}>
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
            {isAdmin && !reorderMode && TRACKED_MODULES.has(item.key) && (
              <ModuleVisitBadge moduleKey={item.key} />
            )}
          </NavLink>
        </div>
      );
    };

    const viewModeOptions = [
      { key: 'default', label: t('nav.viewMode.default'), icon: <List size={14} /> },
      { key: 'custom', label: t('nav.viewMode.custom'), hint: t('nav.viewMode.customHint'), icon: <GripVertical size={14} /> },
      { key: 'categories', label: t('nav.viewMode.categories'), icon: <Layers size={14} /> },
      { key: 'alphabetical', label: t('nav.viewMode.alphabetical'), icon: <ArrowDownAZ size={14} /> },
    ];
    const currentViewIcon = viewModeOptions.find(o => o.key === effectiveMode)?.icon || <List size={14} />;
    const currentViewLabel = viewModeOptions.find(o => o.key === effectiveMode)?.label || t('nav.viewMode.default');

    return (
    <>
      <div className="sidebar-logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
        <img
          src={marca.logoUrl || "/logo.jpg"}
          alt={marca.name}
          onLoad={(e) => setAnchoLogo((e.target.naturalWidth || 1) / (e.target.naturalHeight || 1))}
          style={{ height: 34, width: 'auto', maxWidth: 150, objectFit: 'contain', borderRadius: anchoLogo > 2.5 ? 0 : 8 }}
        />
        {/* Mismo criterio que en el login: un wordmark ancho ya trae el nombre adentro. */}
        {anchoLogo <= 2.5 && (
          <div>
            <div className="sidebar-logo-text">{marca.name}</div>
            <div className="sidebar-logo-sub">{marca.tagline || 'HelpDesk'}</div>
          </div>
        )}
      </div>
      {!marca.esAyudaTecno && (
        <div style={{ padding: '0 14px 10px', marginTop: -6 }}>
          <PoweredByAyudaTecno size="xs" />
        </div>
      )}

      <nav className="sidebar-nav">
        {effectiveMode === 'categories' ? (
          NAV_CATEGORY_ORDER.map(cat => {
            const items = itemsByCategory[cat];
            if (!items || items.length === 0) return null;
            const collapsed = collapsedCats.has(cat);
            return (
              <div key={`cat_${cat}`} style={{ marginBottom: 4 }}>
                <button
                  onClick={() => toggleCollapsedCat(cat)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    padding: '6px 10px', margin: '6px 0 2px',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--c-text-muted)', fontSize: 10, fontWeight: 600,
                    letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'left',
                  }}
                >
                  <ChevronDown size={10} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s' }} />
                  {t(`nav.categories.${cat}`)}
                  <span style={{ marginLeft: 'auto', opacity: 0.6, fontWeight: 500 }}>{items.length}</span>
                </button>
                {!collapsed && items.map((item, idx) => renderNavItem(item, idx, false))}
              </div>
            );
          })
        ) : (
          orderedItems.map((item, idx) => renderNavItem(item, idx, reorderMode))
        )}

        <div className="nav-view-controls" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 8px 0', marginTop: 4, borderTop: '1px solid var(--c-border)', position: 'relative' }} ref={viewMenuRef}>
          <button
            onClick={() => setShowViewMenu(s => !s)}
            className="btn btn-ghost btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--c-text-muted)', padding: '4px 8px', flex: 1, justifyContent: 'flex-start' }}
            title={t('nav.viewMode.title')}
          >
            {currentViewIcon}
            <span style={{ flex: 1, textAlign: 'left' }}>{currentViewLabel}</span>
            <ChevronDown size={10} style={{ transform: showViewMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </button>
          <button
            onClick={() => {
              const next = !reorderMode;
              setReorderMode(next);
              if (next) setNavViewMode('custom');
            }}
            className="btn btn-ghost btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: reorderMode ? 'var(--c-brand)' : 'var(--c-text-muted)', padding: '4px 8px' }}
            title={reorderMode ? t('nav.viewMode.done') : t('nav.viewMode.reorder')}
          >
            <GripVertical size={12} />
            {reorderMode ? t('nav.viewMode.done') : t('nav.viewMode.reorder')}
          </button>
          {(reorderMode || (navOrder && navOrder.length > 0)) && (
            <button
              onClick={resetNavOrder}
              className="btn btn-ghost btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--c-text-muted)', padding: '4px 8px' }}
              title={t('nav.viewMode.reset')}
            >
              <RotateCcw size={10} />
            </button>
          )}
          {showViewMenu && (
            <div
              style={{
                position: 'absolute', bottom: '100%', left: 8, right: 8, marginBottom: 4,
                background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 6,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 100, padding: 4,
              }}
            >
              {viewModeOptions.map(opt => {
                const active = effectiveMode === opt.key;
                const disabled = opt.key === 'custom' && !navOrder && !reorderMode;
                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setNavViewMode(opt.key);
                      if (opt.key !== 'custom' && reorderMode) setReorderMode(false);
                      setShowViewMenu(false);
                    }}
                    disabled={disabled}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '8px 10px', border: 'none', borderRadius: 4,
                      background: active ? 'var(--c-surface-2)' : 'transparent',
                      color: active ? 'var(--c-brand)' : 'var(--c-text)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.4 : 1,
                      fontSize: 12, textAlign: 'left',
                    }}
                  >
                    {opt.icon}
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: active ? 600 : 400 }}>{opt.label}</span>
                      {opt.hint && (
                        <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>{opt.hint}</span>
                      )}
                    </div>
                    {active && <Check size={12} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </nav>
      {/* Plegar/desplegar. Va al pie y no en el encabezado a propósito: arriba
          compite con el logo, y el gesto de "dame más lugar" se busca abajo,
          donde termina la lista. Oculto en móvil por CSS. */}
      <button className="nav-collapse-btn" onClick={toggleNavCollapsed}
        title={navCollapsed ? t('nav.expand') : t('nav.collapse')}
        aria-label={navCollapsed ? t('nav.expand') : t('nav.collapse')}>
        {navCollapsed ? <PanelLeftOpen size={16} /> : <><PanelLeftClose size={16} /> <span>{t('nav.collapse')}</span></>}
      </button>

      <div className="sidebar-bottom">
        <NavLink
          to="/profile"
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          onClick={() => setSidebarOpen(false)}
        >
          <User size={16} />
          {t('nav.profile')}
        </NavLink>
        <button className="nav-item w-full" style={{ border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', color: 'var(--c-text-muted)' }} onClick={handleLogout}>
          <LogOut size={16} />
          {t('auth.logout')}
        </button>
        <button className="kb-help-trigger" onClick={() => setShowKbHelp(true)}>
          <Keyboard size={14} />
          Atajos <kbd style={{ marginLeft: 'auto' }}>?</kbd>
        </button>
        {(isAdmin || showVersionPill) && <VersionPill />}
      </div>
    </>
    );
  };

  // El panel de notificaciones vivía SÓLO dentro del header de escritorio. En el celular
  // el header es otro (rama isMobile) y no lo incluía: el botón "Notificaciones" de la
  // barra inferior cambiaba showNotifications a true y no había nada que dibujar — el
  // botón "no respondía". Thiago lo reportó 3 veces (17 y 23-sep). Ahora se dibuja desde
  // una sola función en los dos headers; el CSS mobile ya lo convierte en hoja inferior.
  const renderNotificationsPanel = () => (showNotifications ? (
                <>
                  {/* El cazador de clicks tiene que quedar JUSTO debajo del panel (500) y por
                      encima del contenido de la página: en 10 quedaba tapado por las barras del
                      detalle de ticket, así que hacer click sobre ellas tampoco cerraba el panel. */}
                  <div style={{ position: 'fixed', inset: 0, zIndex: 499 }} onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setShowNotifications(false))(e); } }} />
                  <div className="notification-dropdown">
                    <div className="notification-dropdown-header">
                      <span>{t('notifications.title')}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {/* OS desktop notification toggle. Three states:
                            - permission 'default' (never asked): show BellOff, click → request
                            - permission 'granted' + enabled:     show BellRing (filled), click → open picker
                            - permission 'granted' + disabled OR 'denied': show BellOff, click → open picker
                            Hidden entirely on iOS Safari (regular tab) since the API is a no-op there. */}
                        {osNotifSupported && !(isIosSafari() && !isStandalonePwa()) && (
                          <div style={{ position: 'relative' }}>
                            <button
                              className="notification-mark-read"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (osNotifPermission === 'default') {
                                  handleEnableOsNotifs();
                                } else {
                                  setShowOsNotifPicker(p => !p);
                                }
                              }}
                              title={osNotifOn ? 'Notificaciones de escritorio activas' : 'Activar notificaciones de escritorio'}
                              style={osNotifOn ? { color: 'var(--c-brand)' } : undefined}
                            >
                              {osNotifOn ? <BellRing size={12} /> : <BellOff size={12} />}
                            </button>
                            {showOsNotifPicker && (
                              <div className="notification-sound-picker" onClick={(e) => e.stopPropagation()} style={{ minWidth: 220 }}>
                                {osNotifPermission === 'denied' ? (
                                  <div style={{ padding: '8px 10px', fontSize: 11, color: 'var(--c-text-muted)' }}>
                                    Permiso bloqueado. Habilitalo desde la configuración del navegador (icono de candado en la URL).
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      className={`notification-sound-option${osNotifOn ? ' active' : ''}`}
                                      onClick={() => {
                                        const turningOn = !osNotifOn;
                                        setOsNotifEnabled(turningOn);
                                        setOsNotifTick(t => t + 1);
                                        if (turningOn) {
                                          tryOsNotify({ title: `${marca.name} ${marca.tagline || ''}`.trim(), body: 'Notificaciones activas ✓', tag: 'os-notif-enabled' });
                                          // (Re)subscribe so push works when the tab is closed
                                          if (isWebPushSupported()) subscribeWebPush().catch(() => {});
                                        } else {
                                          // Unsubscribe so the server stops sending push to this browser
                                          if (isWebPushSupported()) unsubscribeWebPush().catch(() => {});
                                        }
                                      }}
                                    >
                                      {osNotifOn ? '✓ Activadas' : 'Activar notificaciones de escritorio'}
                                    </button>
                                    <button
                                      className={`notification-sound-option${osNotifOnlyHidden ? ' active' : ''}`}
                                      onClick={() => {
                                        setOsNotifOnlyWhenHidden(!osNotifOnlyHidden);
                                        setOsNotifTick(t => t + 1);
                                      }}
                                    >
                                      {osNotifOnlyHidden ? '✓ Solo cuando la pestaña está oculta' : 'Mostrar siempre (incluso enfocado)'}
                                    </button>
                                    <button
                                      className="notification-sound-option"
                                      onClick={() => {
                                        tryOsNotify({ title: `${marca.name} ${marca.tagline || ''}`.trim(), body: 'Notificación de prueba', tag: 'os-notif-test' });
                                      }}
                                    >
                                      Enviar notificación de prueba
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {/* iOS Safari (regular tab): the Notification API is a no-op here.
                            Tell the user to install as PWA to unlock push (iOS 16.4+). */}
                        {isIosSafari() && !isStandalonePwa() && (
                          <button
                            className="notification-mark-read"
                            title="iOS Safari no soporta notificaciones en pestañas. Tocá Compartir → Agregar a pantalla de inicio para instalar la app y recibir notificaciones."
                            onClick={(e) => {
                              e.stopPropagation();
                              toast('Para notificaciones en iPhone: Compartir → Agregar a pantalla de inicio, abrí la app desde el ícono y activá las notificaciones desde la campana.', { duration: 8000 });
                            }}
                          >
                            <BellOff size={12} />
                          </button>
                        )}
                        {/* Sonidos: uno por tipo de aviso. Antes era una sola lista
                            que aplicaba a todo; el punto de separarlos es poder saber
                            QUÉ sonó sin mirar la pantalla. Cada fila tiene su botón de
                            escucha, que es lo que hace la elección posible: nadie sabe
                            de memoria cómo suena "Win98 Notify". */}
                        <div style={{ position: 'relative' }}>
                          <button
                            className="notification-mark-read"
                            onClick={(e) => { e.stopPropagation(); setShowSoundPicker(!showSoundPicker); }}
                            title={t('sounds.title')}
                          >
                            {allSoundsOff ? <VolumeX size={12} /> : <Volume2 size={12} />}
                          </button>
                          {showSoundPicker && (
                            <div className="notification-sound-picker notification-sound-matrix" onClick={(e) => e.stopPropagation()}>
                              <div className="notification-sound-hint">{t('sounds.hint')}</div>
                              {SOUND_EVENTS.map(ev => (
                                <div key={ev.key} className="notification-sound-row">
                                  <span className="notification-sound-label">{t(ev.i18n)}</span>
                                  <select
                                    className="form-select notification-sound-select"
                                    value={soundMap[ev.key] ?? getSoundFor(ev.key)}
                                    onChange={(e) => {
                                      const id = e.target.value;
                                      setSoundFor(ev.key, id);
                                      setSoundMap(m => ({ ...m, [ev.key]: id }));
                                      playSound(id);   // se escucha al elegirlo, sin un click extra
                                    }}
                                  >
                                    {/* "Sin sonido" arriba y suelto; el resto agrupado,
                                        para que los suaves se vean primero sin esconder
                                        los retro de quien ya los eligió. */}
                                    <option value="none">{t('sounds.names.none')}</option>
                                    {SOUND_GROUPS.map(grp => (
                                      <optgroup key={grp.key} label={t(grp.i18n)}>
                                        {NOTIFICATION_SOUNDS.filter(snd => snd.group === grp.key).map(snd => (
                                          <option key={snd.id} value={snd.id}>
                                            {snd.i18n ? t(snd.i18n, snd.label) : snd.label}
                                          </option>
                                        ))}
                                      </optgroup>
                                    ))}
                                  </select>
                                  <button
                                    className="notification-sound-play"
                                    title={t('sounds.preview')}
                                    onClick={(e) => { e.stopPropagation(); playSound(soundMap[ev.key] ?? getSoundFor(ev.key)); }}
                                  >
                                    <Volume2 size={11} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {totalUnread > 0 && (
                          <button className="notification-mark-read" onClick={markAllReadCombined}>
                            <Check size={12} /> {t('notifications.markAllRead')}
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Envuelto a propósito: si esta fila llegara a romperse, se cae
                        sola y la campana sigue funcionando. Sin esto, un error acá se
                        propaga al Layout y con él a TODAS las pantallas — que es
                        exactamente lo que pasó el 8-sep con la primera versión. */}
                    {showNotifFilter && (
                      <SilentBoundary name="NotifFilter">
                      <div className="notification-filter-row">
                        <button
                          className={`notification-filter-chip${notifFilterActive === 'all' ? ' active' : ''}`}
                          onClick={() => setNotifFilter('all')}
                        >
                          {t('notifications.filterAll')}
                        </button>
                        {notifSections.map(sec => (
                          <button
                            key={sec.key}
                            className={`notification-filter-chip${notifFilterActive === sec.key ? ' active' : ''}`}
                            onClick={() => setNotifFilter(sec.key)}
                          >
                            {sec.label} <span className="notification-filter-count">{sec.n}</span>
                          </button>
                        ))}
                      </div>
                      </SilentBoundary>
                    )}
                    <div className="notification-dropdown-list">
                      {ticketNotifications.length === 0 && notifications.length === 0 && chatNotifications.length === 0 && invoiceNotifications.length === 0 && paymentNotifications.length === 0 && errandNotifications.length === 0 && inAppNotifs.length === 0 ? (
                        <div className="notification-empty">
                          <Bell size={20} />
                          <span>{t('notifications.noNotifications')}</span>
                        </div>
                      ) : (
                        <>
                          {/* In-app notifications (lifecycle/SLA) — derived from inAppNotifs minus scheduled-send types */}
                          {showSection('activity') && activityNotifs.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Activity size={12} /> {t('notifications.activity')}
                              </div>
                              {activityNotifs.slice(0, 10).map(n => {
                                const emoji = { ticket_new: '🎫', ticket_assigned: '📌', ticket_reply: '💬', sla_warning: '⚠️', sla_breach: '🚨', ticket_stale: '🕐', internal_note: '📝', ticket_status_changed: '📋', client_waiting: '⏳' }[n.type] || '🔔';
                                const ago = n.created_at ? (() => {
                                  const mins = Math.round((Date.now() - new Date(n.created_at).getTime()) / 60000);
                                  if (mins < 1) return 'ahora';
                                  if (mins < 60) return `${mins}m`;
                                  if (mins < 1440) return `${Math.floor(mins / 60)}h`;
                                  return `${Math.floor(mins / 1440)}d`;
                                })() : '';
                                return (
                                  <div
                                    key={n.id}
                                    className="notification-item"
                                    style={{ cursor: 'pointer', opacity: n.read ? 0.6 : 1 }}
                                    onClick={() => {
                                      if (!n.read) markOneInAppRead(n.id);
                                      if (n.data?.link) { navigate(n.data.link); setShowNotifications(false); }
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                      <span style={{ fontSize: 14, lineHeight: 1 }}>{emoji}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: n.read ? 400 : 600, color: 'var(--c-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {n.title}
                                        </div>
                                        <div style={{ fontSize: 11, color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {(n.body || '').replace(/^[^\s]+\s/, '').slice(0, 50)}
                                        </div>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                                        {!n.read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6' }} />}
                                        <span style={{ fontSize: 10, color: 'var(--c-text-muted)', whiteSpace: 'nowrap' }}>{ago}</span>
                                        {/* Hard-dismiss: removes the row so the next 30s poll can't bring it back */}
                                        <button
                                          title={t('notifications.dismiss')}
                                          onClick={(e) => { e.stopPropagation(); dismissInAppNotif(n.id); }}
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}
                                        >
                                          <X size={11} />
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}

                          {/* Tickets section (always) */}
                          {showSection('tickets') && ticketNotifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Ticket size={11} />
                                {t('notifications.tickets')}
                                <span className="notification-section-count">{ticketNotifications.length}</span>
                              </div>
                              {ticketNotifications.map((tk) => {
                                const navId = tk._isInternalNote ? tk.ticket_id : tk.id;
                                return (
                                <div
                                  key={tk.id}
                                  className="notification-item"
                                  data-href={`/tickets/${navId}`}
                                  onClick={() => { navigate(`/tickets/${navId}`); setShowNotifications(false); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {tk._isInternalNote
                                    ? <span className="notification-ticket-priority" style={{ background: '#6b7280' }} />
                                    : tk.status === 'validation'
                                      ? <span className="notification-otp-badge"><KeyRound size={12} /></span>
                                      : <span className="notification-ticket-priority" style={{ background: PRIORITY_COLORS[tk.priority] || PRIORITY_COLORS.normal }} />}
                                  <div className="notification-item-body">
                                    <span className="notification-item-text">
                                      <strong>{tk._isInternalNote ? `📝 #${tk.ticket_number}` : tk.status === 'validation' ? 'OTP' : `#${tk.ticket_number}`}</strong>{' '}
                                      {tk.subject?.length > 35 ? tk.subject.slice(0, 35) + '...' : tk.subject}
                                    </span>
                                    <span className="notification-item-time">
                                      {tk._isInternalNote
                                        ? `${tk._noteAuthor}: ${tk._notePreview || 'Nota interna'}${tk.company_name ? ` · ${tk.company_name}` : ''}`
                                        : <>
                                            {tk.contact_name || tk.contact_email}{tk.company_name ? ` · ${tk.company_name}` : ''}
                                            {' — '}
                                            {tk.assigned_to
                                              ? `${tk.agent_first_name || ''} ${tk.agent_last_name || ''}`.trim() || t('notifications.assigned')
                                              : t('notifications.unassigned')}
                                          </>}
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                        {timeAgo(tk._notifTime || tk.created_at)}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                                );
                              })}
                            </>
                          )}

                          {/* Field Tasks section */}
                          {showSection('errands') && errandNotifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <ClipboardList size={11} />
                                {t('errands.title')}
                                <span className="notification-section-count">{errandNotifications.length}</span>
                              </div>
                              {errandNotifications.map((er) => (
                                <div
                                  key={er.id}
                                  className="notification-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => { navigate('/field-tasks'); setShowNotifications(false); }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, background: '#05966915', color: '#059669', flexShrink: 0 }}>
                                    <ClipboardList size={12} />
                                  </span>
                                  <div className="notification-item-body">
                                    <span className="notification-item-text">
                                      <strong>#{er.errand_number}</strong>{' '}
                                      {er.title?.length > 35 ? er.title.slice(0, 35) + '...' : er.title}
                                      {er.priority && er.priority !== 'normal' && (() => {
                                        const pc = ERRAND_PRIORITY_COLORS[er.priority] || '#6b7280';
                                        return (
                                          <span style={{ marginLeft: 4, fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600, color: pc, background: `${pc}18` }}>
                                            {ERRAND_PRIORITY_LABELS[er.priority] || er.priority}
                                          </span>
                                        );
                                      })()}
                                    </span>
                                    {er.due_date && (
                                      <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                                        Vence: {new Date(er.due_date).toLocaleDateString('es-AR')}
                                      </span>
                                    )}
                                    <span className="notification-item-time">
                                      {er._type === 'completed'
                                        ? `Completado por ${er._completedBy || ''}`
                                        : `${er.creator_first_name || ''} ${er.creator_last_name || ''}`.trim()}
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                        {timeAgo(er._notifTime || er.created_at)}
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Invoices section (admin only) */}
                          {showSection('invoices') && (isAdmin || isDemo) && invoiceNotifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Receipt size={11} />
                                {t('notifications.invoices')}
                                <span className="notification-section-count">{invoiceNotifications.reduce((s, i) => s + i.unread_count, 0)}</span>
                              </div>
                              {invoiceNotifications.map((inv) => (
                                <div
                                  key={inv.id}
                                  className="notification-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    // Persist mark-read for THIS invoice (also clears any system_notifications
                                    // tied to it so the bell can't bring them back on the next 30s poll)
                                    api.patch(`/invoices/${inv.id}/messages-read`).catch(() => {});
                                    setInvoiceNotifications(prev => prev.filter(p => p.id !== inv.id));
                                    setInvoiceUnreadCount(prev => Math.max(0, prev - inv.unread_count));
                                    navigate('/invoicing');
                                    setShowNotifications(false);
                                  }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, background: '#0891b215', color: '#0891b2', flexShrink: 0 }}>
                                    <Receipt size={12} />
                                  </span>
                                  <div className="notification-item-body" style={{ flex: 1 }}>
                                    <span className="notification-item-text">
                                      <strong>{inv.invoice_number ? `#${inv.invoice_number}` : 'Factura'}</strong>{' '}
                                      {inv.company_name}
                                    </span>
                                    <span className="notification-item-time">
                                      {inv.unread_count} {inv.unread_count === 1 ? 'mensaje sin leer' : 'mensajes sin leer'}
                                      {inv.last_message_at && (
                                        <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                          {timeAgo(inv.last_message_at)}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                  {/* Hard-dismiss for this one invoice — marks its messages read without navigating */}
                                  <button
                                    title="Marcar como leído"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      api.patch(`/invoices/${inv.id}/messages-read`).catch(() => {});
                                      setInvoiceNotifications(prev => prev.filter(p => p.id !== inv.id));
                                      setInvoiceUnreadCount(prev => Math.max(0, prev - inv.unread_count));
                                    }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted)', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Programados section (scheduled sends completed) */}
                          {showSection('scheduled') && (isAdmin || isDemo) && systemUnread.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Clock size={11} />
                                {t('notifications.scheduled')}
                                <span className="notification-section-count">{systemUnread.length}</span>
                                <button
                                  style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--c-text-muted)', cursor: 'pointer', fontSize: 10, padding: '2px 4px' }}
                                  onClick={(e) => { e.stopPropagation(); markSystemRead(); }}
                                >
                                  {t('notifications.markRead')}
                                </button>
                              </div>
                              {systemUnread.slice(0, 10).map((n) => (
                                <div
                                  key={n.id}
                                  className="notification-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    // Mark THIS one read instead of nuking all (less surprising UX).
                                    markOneInAppRead(n.id);
                                    navigate(n.data?.link || '/invoicing');
                                    setShowNotifications(false);
                                  }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, background: n.type === 'scheduled_wa_sent' ? '#22c55e15' : '#f59e0b15', color: n.type === 'scheduled_wa_sent' ? '#22c55e' : '#f59e0b', flexShrink: 0 }}>
                                    <CheckCircle2 size={12} />
                                  </span>
                                  <div className="notification-item-body" style={{ flex: 1 }}>
                                    <span className="notification-item-text">
                                      {n.title}
                                    </span>
                                    <span className="notification-item-time">
                                      {n.body}
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                        {timeAgo(n.created_at)}
                                      </span>
                                    </span>
                                  </div>
                                  {/* Hard-dismiss this row */}
                                  <button
                                    title={t('notifications.dismiss')}
                                    onClick={(e) => { e.stopPropagation(); dismissInAppNotif(n.id); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-muted)', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Payments section (all internal non-scoped users) */}
                          {showSection('payments') && paymentNotifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Wallet size={11} />
                                {t('notifications.payments')}
                                <span className="notification-section-count">{paymentNotifications.length}</span>
                              </div>
                              {paymentNotifications.map((pmt) => {
                                const fmtAmt = parseFloat(pmt.amount_ars || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                                const methodLabels = { bank_transfer: 'Transferencia', cash: 'Efectivo', check: 'Cheque' };
                                return (
                                <div
                                  key={pmt.id}
                                  className="notification-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => { navigate('/my-payroll'); setShowNotifications(false); }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 4, background: '#22c55e15', color: '#22c55e', flexShrink: 0 }}>
                                    <Wallet size={12} />
                                  </span>
                                  <div className="notification-item-body" style={{ flex: 1 }}>
                                    <span className="notification-item-text">
                                      <strong style={{ color: '#22c55e' }}>${fmtAmt}</strong>
                                      <span style={{
                                        marginLeft: 6, display: 'inline-flex', alignItems: 'center',
                                        padding: '1px 6px', borderRadius: 4, fontSize: 11,
                                        background: '#22c55e15', border: '1px solid #22c55e40',
                                        color: '#22c55e',
                                      }}>
                                        {methodLabels[pmt.payment_method] || pmt.payment_method}
                                      </span>
                                    </span>
                                    <span className="notification-item-time">
                                      {pmt.from_first} {pmt.from_last}{pmt.period_label ? ` · ${pmt.period_label}` : ''}
                                      <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                        {timeAgo(pmt.created_at)}
                                      </span>
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                    <button
                                      className="btn btn-sm"
                                      style={{ fontSize: 11, padding: '2px 8px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4 }}
                                      onClick={(e) => { e.stopPropagation(); confirmPaymentFromBell(pmt.id); }}
                                    >
                                      <Check size={10} style={{ marginRight: 3 }} />Confirmar
                                    </button>
                                    {pmt.receipt_url && (
                                      <a
                                        href={`${api.defaults.baseURL?.replace('/api', '')}${pmt.receipt_url}`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="btn btn-sm"
                                        style={{ fontSize: 10, padding: '1px 6px', background: 'var(--c-surface-2)', color: 'var(--c-text-muted)', border: '1px solid var(--c-border)', borderRadius: 4, textAlign: 'center', textDecoration: 'none' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Comprobante
                                      </a>
                                    )}
                                  </div>
                                </div>
                                );
                              })}
                            </>
                          )}

                          {/* Chat section (all roles) */}
                          {showSection('chat') && chatNotifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <MessageSquare size={11} />
                                {t('notifications.chat')}
                                <span className="notification-section-count">{chatNotifications.length}</span>
                              </div>
                              {chatNotifications.map((cn) => (
                                <div
                                  key={cn.id}
                                  className="notification-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => { openFloatingConversation(cn.conversationId); setShowNotifications(false); }}
                                >
                                  <div style={{ fontSize: 16, lineHeight: 1, flexShrink: 0, width: 20, textAlign: 'center' }}>💬</div>
                                  <div className="notification-item-body">
                                    <span className="notification-item-text">
                                      <strong>{cn.senderName}</strong>{' '}
                                      {cn.preview?.length > 40 ? cn.preview.slice(0, 40) + '...' : cn.preview}
                                    </span>
                                    <span className="notification-item-time">
                                      {timeAgo(cn.time)}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}

                          {/* Agents section (manager/admin only) */}
                          {showSection('agents') && isManager && notifications.length > 0 && (
                            <>
                              <div className="notification-section-label">
                                <Users size={11} />
                                {t('notifications.agents')}
                                <span className="notification-section-count">{notifications.length}</span>
                              </div>
                              {notifications.map((item) => {
                                const agentInitials = (item.agent.name || '')
                                  .split(' ')
                                  .map(w => w[0])
                                  .join('')
                                  .toUpperCase()
                                  .slice(0, 2);
                                return (
                                  <div key={item.id} className="notification-item">
                                    <div className="notification-item-avatar">
                                      {item.agent.avatar_url
                                        ? <img src={item.agent.avatar_url} alt="" />
                                        : <span>{agentInitials}</span>}
                                      <span
                                        className="presence-toast-dot"
                                        style={{ background: item.isOnline ? '#22c55e' : '#6b7280' }}
                                      />
                                    </div>
                                    <div className="notification-item-body">
                                      <span className="notification-item-text">
                                        <strong>{item.agent.name}</strong>{' '}
                                        {item.isOnline ? t('presence.cameOnline') : t('presence.wentOffline')}
                                      </span>
                                      <span className="notification-item-time">
                                        {timeAgo(item.time)}
                                        <span style={{ display: 'block', fontSize: 10, color: 'var(--c-text-muted)', opacity: 0.7 }}>
                                          {new Date(item.time).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
  ) : null);

  return (
    <div className={`app-layout${navCollapsed ? ' nav-collapsed' : ''}`} style={{ paddingTop: (isImpersonating ? 34 : 0) + (isOnline ? 0 : 32) }}>
      <ImpersonationBanner />
      {!isOnline && (
        <div style={{
          position: 'fixed', top: isImpersonating ? 34 : 0, left: 0, right: 0, zIndex: 9998,
          background: '#ef4444', color: '#fff', textAlign: 'center',
          fontSize: 13, fontWeight: 500, padding: '6px 12px',
        }}>
          Sin conexión — las acciones se reanudarán cuando vuelvas a estar en línea
        </div>
      )}
      {showBirthday && (
        <BirthdayOverlay name={user?.firstName} onDone={() => setShowBirthday(false)} />
      )}
      {showAndy && ReactDOM.createPortal(
        <div onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setShowAndy(false))(e); } }} style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999,
          background: 'rgba(0,0,0,0.85)', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          animation: 'fadeIn 0.3s ease'
        }}>
          <img src="/andy-beach.jpeg" alt="Andy on the beach" style={{
            maxWidth: '60vw', maxHeight: '60vh', borderRadius: 16,
            boxShadow: '0 0 60px rgba(255,255,255,0.2)', animation: 'konamiZoom 0.5s ease-out'
          }} />
          <div style={{ color: '#fff', fontSize: 24, fontWeight: 800, marginTop: 20, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
            You found the boss level
          </div>
          <div style={{ color: '#aaa', fontSize: 12, marginTop: 6 }}>click anywhere to close</div>
          <style>{`
            @keyframes konamiZoom { from { transform: scale(0) rotate(-10deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
          `}</style>
        </div>,
        document.body
      )}
      {showBlues && ReactDOM.createPortal(
        <BluesBrothersOverlay onClose={() => setShowBlues(false)} />,
        document.body
      )}
      {sidebarOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 40 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setSidebarOpen(false))(e); } }}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={{ zIndex: sidebarOpen ? 50 : 'auto' }}>
        <SidebarContent />
      </aside>

      <div className="main-content">
        <header className="header">
          {isMobile ? (
            /* Mobile header: logo + search + theme */
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <img src="/logo.jpg" alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--c-text)' }}>{marca.name}</span>
              </div>
              <div className="header-actions">
                {isStandaloneMode() && (
                  <button
                    className="btn btn-ghost btn-icon"
                    onClick={() => window.location.reload()}
                    style={{ padding: 8 }}
                    aria-label="Reload"
                  >
                    <RefreshCw size={16} />
                  </button>
                )}
                <button
                  className="notification-bell"
                  onClick={() => setShowCommandPalette(true)}
                  aria-label="Search"
                >
                  <Search size={18} />
                </button>
                {!isClient && <WelcomeHelpButton />}
                {!isClient && marca.esAyudaTecno && <PendingActionsBell />}
                <button
                  className="btn btn-ghost btn-icon"
                  onClick={toggleTheme}
                  style={{ padding: 8 }}
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
                </button>
              </div>
            </>
          ) : (
            /* Desktop header */
            <>
              <button
                className="btn btn-ghost btn-icon"
                style={{ display: 'none' }}
                onClick={() => setSidebarOpen(true)}
                id="mobile-menu-btn"
              >
                <Menu size={20} />
              </button>

              <div className="header-title">
                <HeaderClock />
                {isAdmin && <ServerLoadIndicator />}
                <PinnedTicketsBar />
              </div>

              <div className="header-actions">
                {/* theAndTech — el medio de tecnología de Andy.
                    Sólo en NUESTRA instancia: en el helpdesk de un cliente sería publicidad
                    de otra marca adentro de su plataforma, justo lo contrario del white-label.
                    La clase `theandtech-link` no es decorativa: el heatmap registra cada click
                    con su selector, así que después se puede saber quién entró sin agregar
                    ningún seguimiento nuevo. */}
                {marca.esAyudaTecno && (
                  <a
                    href="https://theandtech.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="theandtech-link"
                    data-tooltip="theAndTech — tecnología, en criollo"
                  >
                    <Zap size={13} className="theandtech-bolt" />
                    <span>theandtech<span className="theandtech-dim">.com</span></span>
                  </a>
                )}

                <div style={{ position: 'relative' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => updateLanguage(user?.language === 'en' ? 'es' : 'en')}
                    data-tooltip={user?.language === 'en' ? 'Switch to Español' : 'Switch to English'}
                    style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    <Globe size={14} />
                    {user?.language?.toUpperCase() || 'ES'}
                  </button>
                </div>

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={toggleTheme}
                  data-tooltip={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                </button>

                {/* Andy: un solo botón "Plataformas" con todo lo que construimos, entrando
                    ya logueado donde hay puente. El resto del equipo: entrada directa al
                    helpdesk de un tenant (Wolfsohn); se auto-oculta sin puente. */}
                {canUseMyPlatforms(user, isImpersonating)
                  ? <PlatformLauncher />
                  : (!isClient && <InstanceSwitcher />)}

                {/* How does the game work? — internal users only */}
                {!isClient && <WelcomeHelpButton />}
                {!isClient && marca.esAyudaTecno && <PendingActionsBell />}

                {/* Search (Cmd+K) */}
                <button
                  className="notification-bell"
                  onClick={() => setShowCommandPalette(true)}
                  data-tooltip="Search (⌘K)"
                  style={{ marginRight: -4 }}
                >
                  <Search size={18} />
                </button>

            {/* Phone Directory */}
            <PhonebookPopover isAdmin={isAdmin} />

            {/* Notification Bell — visible for all roles */}
            <div style={{ position: 'relative' }}>
              <button
                className="notification-bell"
                onClick={() => { setShowNotifications(!showNotifications); setShowUserMenu(false); }}
              >
                <Bell size={18} />
                {totalUnread > 0 && (
                  <span className="notification-badge">
                    {totalUnread > 9 ? '9+' : totalUnread}
                  </span>
                )}
              </button>

              {renderNotificationsPanel()}
            </div>

            <WhatsNewPopover />

            <div style={{ position: 'relative' }}>
              <div className="user-menu" onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false); }}>
                <div className="user-avatar">
                  {user?.avatarUrl
                    ? <img src={user.avatarUrl} alt="" />
                    : initials}
                </div>
                <span className="user-name">{user?.firstName}</span>
                <ChevronDown size={12} style={{ color: 'var(--c-text-muted)' }} />
              </div>

              {showUserMenu && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onMouseDown={(e) => { if (e.target === e.currentTarget) { (() => setShowUserMenu(false))(e); } }} />
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                    background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                    borderRadius: 'var(--radius-lg)', minWidth: 180, zIndex: 20,
                    boxShadow: 'var(--shadow-lg)', overflow: 'hidden'
                  }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--c-border)' }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{user?.firstName} {user?.lastName}</div>
                      <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{user?.email}</div>
                    </div>
                    <NavLink to="/profile" className="nav-item" style={{ borderRadius: 0, margin: 0 }} onClick={() => setShowUserMenu(false)}>
                      <User size={14} /> {t('nav.profile')}
                    </NavLink>
                    <button
                      onClick={handleLogout}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 12px', background: 'none', border: 'none', color: '#ef4444', fontSize: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
                    >
                      <LogOut size={14} /> {t('auth.logout')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
            </>
          )}
        </header>

        <style>{`
          @media (max-width: 768px) {
            #mobile-menu-btn { display: flex !important; }
            .header-clock { display: none !important; }
          }
        `}</style>

        {/* Ticker de alertas de NUESTRA infraestructura: no aplica a la instancia de un cliente. */}
        {marca.esAyudaTecno && <AlertTicker />}
        {marca.esAyudaTecno && <ActionNeededBanner />}
        {/* Inside .main-content on purpose: .app-layout is a flex ROW, so as its
            last child this banner became a third column whose max-content width
            squeezed .main-content (min-width:0) to nothing — every page was a
            blue wall for any agent without Google Calendar connected. */}
        {hasModule('calendar') && <CalendarConnectionBanner />}

        <main className="page">
          <PendingReviewBanner />
          {marca.esAyudaTecno && <AgentActionsModal />}
          <PageBoundary locationKey={location.key}>
            <Outlet />
          </PageBoundary>
        </main>

        {hasModule('chat') && <SilentBoundary name="FloatingChat"><FloatingChat /></SilentBoundary>}
        {hasModule('whatsapp') && <SilentBoundary name="WhatsAppBubble"><WhatsAppBubble /></SilentBoundary>}
        {/* Hermana de la de WhatsApp: la razón por la que los tickets quedan sin atender
            y los WhatsApp no es que uno EMPUJA y el otro hay que ir a buscarlo. */}
        {/* SIN hasModule a propósito: 'tickets' no es un módulo toggleable — no está en
            getModuleDefaults() ni en el mapa del frontend, porque los tickets son el
            corazón del helpdesk y los ve todo usuario interno. Gatearlo con
            hasModule('tickets') devolvía !!undefined = false para TODOS menos los
            admins (que devuelven true antes de mirar el mapa), así que la burbuja
            existía sólo para Andy. Ariel la reportó el 7/9: "no veo bubble de tickets". */}
        <SilentBoundary name="TicketsBubble"><TicketsBubble /></SilentBoundary>
        <RemoteAssistBubble />
        {hasModule('projects') && <SilentBoundary name="FloatingProjectTimer"><FloatingProjectTimer /></SilentBoundary>}
        {/* Gamificación interna nuestra. En la ticketera de una escuela no pinta nada. */}
        {marca.esAyudaTecno && <SilentBoundary name="PokemonOverlay"><PokemonOverlay /></SilentBoundary>}
        <AdminViewAsWidget />
        <DockDropHint />
        <SilentBoundary name="IncomingCall">
          <IncomingCallOverlay
            incomingCall={incomingCall}
            onAccept={acceptCall}
            onReject={rejectCall}
          />
        </SilentBoundary>
        <SilentBoundary name="ActiveCall">
          <ActiveCallOverlay
            callState={callState}
            onEndCall={endCall}
            onToggleVideo={toggleVideo}
            onToggleAudio={toggleAudio}
            conversations={callConversations}
          />
        </SilentBoundary>
      </div>
      {isMobile && renderNotificationsPanel()}
      {isMobile && (
        <MobileBottomNav
          ticketBadge={ticketUnreadCount}
          notificationCount={totalUnread}
          // Alterna, no abre y punto. Antes era setSidebarOpen(true): apretabas "Más",
          // se desplegaba, y volver a apretarlo no hacía nada porque ya estaba en true.
          // Había que tocar afuera para cerrarlo. Thiago lo reportó desde el celular el
          // 17-sep: "si intentás ocultarlas usando el mismo botón no funciona".
          onOpenSidebar={() => setSidebarOpen(prev => !prev)}
          onOpenNotifications={() => setShowNotifications(prev => !prev)}
        />
      )}
      <KeyboardShortcutsHelp isOpen={showKbHelp} onClose={() => setShowKbHelp(false)} />
      <CommandPalette isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
      {hasModule('feedback_requests') && (
        <QuickBugModal open={showQuickBug} onClose={() => setShowQuickBug(false)} />
      )}
      {showProfileCompletion && user && (
        <ProfileCompletionModal
          user={user}
          onComplete={() => { setShowProfileCompletion(false); window.location.reload(); }}
          onSkip={() => {
            try { localStorage.setItem(`profile_completion_skipped_v1_${user.id}`, Date.now()); } catch { /* modo privado */ }
            setShowProfileCompletion(false);
          }}
        />
      )}
      {showOnboarding && user && (
        <AgentOnboardingModal
          onClose={() => setShowOnboarding(false)}
          onStartTour={() => setShowTour(true)}
        />
      )}
      {showBienvenida && user && (
        <TenantWelcomeWizard userId={user.id} onClose={() => setShowBienvenida(false)} />
      )}
      {showTour && <GuidedTour onClose={() => setShowTour(false)} />}
    </div>
  );
}
